/**
 * @file LayoutTab.js
 * @description Portal page layout editor component that manages the grid-based layout functionality.
 * This component allows users to create, position, resize, and configure containers within a responsive grid,
 */
import {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useMemo,
    useRef,
    useContext,
} from 'react';
import _ from 'lodash';
import { NewerPropertiesModal } from './NewerPropertiesModal';
import { PropertiesModal } from './NewPropertiesModal';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { fetchTemplates } from '../../services/templateService';
import './NewPortalPage.css';
import './NewPortalLayout.css';
import '../fluid-grid/FreeformGrid.css';
import '../NewComponent.css';
import { Component } from '../Component';
import Tooltip from '../ui/tooltips/Tooltip';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { ContainerTypes, TenantTiers } from '../../utils/data/enums';
import {
    readPortalPage,
    updatePortalPage,
    createPortalPage,
    deletePortalPage,
} from '../../services/portalPageService';
import {
    createContainer,
    deleteContainer,
    updateContainer,
} from '../../services/containerService';
import { cloneDeep } from 'lodash';
import {
    getOverlappingContainerIds,
    hasContainerOverlap,
} from '../../utils/overlapUtils';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const NewPortalLayout = forwardRef(
    (
        {
            selectedResolution,
            portalId,
            savedPage,
            saveGrid,
            deleteContainers,
            onOverlapChange,
            isActivePage = false,
            hasDraft = false,
            onDraftCreated,
        },
        ref,
    ) => {
        const activePortalIdRef = useRef(portalId);
        const isActivePageRef = useRef(isActivePage);
        const hasDraftRef = useRef(hasDraft);
        const [propertiesModal, setPropertiesModal] = useState();
        const [templates, setTemplates] = useState([]);
        const [templatesType, setTemplatesType] = useState('Custom');
        const [savedItem, setSavedItem] = useState(null);
        const [isLoading, setIsLoading] = useState(false);
        const [isAddContainerOpen, setIsAddContainerOpen] = useState(false);
        const [autoMobileLayout, setAutoMobileLayout] = useState(false);
        const { showModal } = useContext(ModalContext);

        const [containers, setContainers] = useState([]);
        const [mobileConfig, setMobileConfig] = useState([]);
        const [isSaving, setIsSaving] = useState(false);
        const [newContainers, setNewContainers] = useState([]);
        const [overlappingIds, setOverlappingIds] = useState(new Set());
        const { clientTierMeetsRequirement } = useAuthContext();

        // useImperativeHandle to expose this function to the parent component
        useImperativeHandle(ref, () => ({
            openAddContainerModal: () => {
                if (!isSaving) {
                    setIsAddContainerOpen(true);
                }
            },
            resetMobileLayout: () => {
                if (!isSaving) {
                    setAutoMobileLayout(true);
                    handleResetMobileLayout();
                }
            },
            saveLayoutGrid: async (isAutoSave = false) => {
                if (isSavingRef.current) {
                    return false;
                }
                return handleSaveLayoutGrid(isAutoSave);
            },
            checkForChanges: async () => {
                return await checkForChanges();
            },
        }));

        useEffect(() => {
            setTemplatesType('Custom');
        }, [isAddContainerOpen]);

        useEffect(function initializeGrid() {
            //This function runs one when initializing the grid
            loadContainers();
        }, []);

        /* LOAD CONTAINERS */
        async function loadContainers() {
            setIsLoading(true);
            try {
                const fetchedPortalPage = await readPortalPage(
                    activePortalIdRef.current,
                    true,
                );
                const { viewModels, containers } = fetchedPortalPage;

                setContainers([]);
                setNewContainers([]);
                setMobileRenderArray([]);

                await new Promise(resolve => setTimeout(resolve, 100));

                setContainers(containers);

                const updatedContainers = containers.map(container => ({
                    ...container,
                    temporaryId: container.id,
                }));

                setNewContainers(updatedContainers);

                const portalData = viewModels[0] || [];
                const mobileConfiguration =
                    portalData.mobileConfiguration || [];
                setMobileConfig(mobileConfiguration);

                if (mobileConfiguration.length > 0) {
                    setMobileRenderArray(mobileConfiguration);
                } else {
                    setAutoMobileLayout(true);
                }
            } catch (e) {
                console.error('Error fetching portal page:', e);
                Toast.error(getText('msg_3f967464317a'));
            } finally {
                setIsLoading(false);
            }
        }

        async function refreshContainers() {
            try {
                const fetchedPortalPage = await readPortalPage(
                    activePortalIdRef.current,
                    true,
                );
                const { viewModels, containers } = fetchedPortalPage;

                setContainers(containers);

                const updatedContainers = containers.map(container => ({
                    ...container,
                    temporaryId: container.id,
                }));

                setNewContainers(updatedContainers);

                const portalData = viewModels[0] || [];
                const mobileConfiguration =
                    portalData.mobileConfiguration || [];
                setMobileConfig(mobileConfiguration);
                if (mobileConfiguration.length > 0) {
                    setMobileRenderArray(mobileConfiguration);
                } else {
                    setAutoMobileLayout(true);
                }

                //setOriginalPage(portalData);
            } catch (e) {
                console.error('Error refreshing containers:', e);
            }
        }

        /* CHECK IF ANY UNSAVED CHANGES EXIST IN LAYOUT GRID */
        const checkForChanges = async () => {
            const currentContainers = containersRef.current;
            const currentNewContainers = newContainersRef.current;

            const fetchedContainerMap = new Map(
                currentContainers.map(container => [container.id, container]),
            );

            const containersToCreate = [];
            const containersToUpdate = [];
            const currentContainerIds = new Set(
                currentNewContainers.map(container => container.id),
            );

            for (const container of currentNewContainers) {
                if (container.id === null) {
                    containersToCreate.push(container);
                } else {
                    const existingContainer = fetchedContainerMap.get(
                        container.id,
                    );
                    if (existingContainer) {
                        let temporaryContainerCopy = cloneDeep(container);
                        delete temporaryContainerCopy.temporaryId;
                        delete temporaryContainerCopy.populationGroups;
                        delete temporaryContainerCopy.isNew;

                        let existingContainerCopy =
                            cloneDeep(existingContainer);
                        delete existingContainerCopy.temporaryId;
                        delete existingContainerCopy.populationGroups;
                        delete existingContainerCopy.isNew;

                        if (
                            !_.isEqual(
                                temporaryContainerCopy,
                                existingContainerCopy,
                            )
                        ) {
                            containersToUpdate.push(container);
                        }
                    }
                }
            }

            const containersToDelete = currentContainers
                .filter(container => !currentContainerIds.has(container.id))
                .map(container => container.id);

            const mobileConfigChanged = !areConfigEqual(
                mobileRenderArrayRef.current,
                mobileConfigRef.current,
            );

            // Return true if there are changes to save
            return (
                containersToCreate.length > 0 ||
                containersToDelete.length > 0 ||
                containersToUpdate.length > 0 ||
                mobileConfigChanged
            );
        };

        /* SAVE LAYOUT GRID */
        const handleSaveLayoutGrid = async (isAutoSave = false) => {
            if (isSavingRef.current) {
                return false;
            }

            if (!isAutoSave && !(await checkForChanges())) {
                Toast.info('There are no changes to save.');
                return false;
            }

            isSavingRef.current = true;
            setIsSaving(true);

            let currentContainers = containersRef.current;
            let currentNewContainers = newContainersRef.current;

            if (
                isActivePageRef.current &&
                !hasDraftRef.current &&
                !savedPage.isTemplate
            ) {
                hasDraftRef.current = true;

                if (!isAutoSave) {
                    setIsLoading(true);
                }

                let draftId = null;

                try {
                    const livePortalId = activePortalIdRef.current;
                    const currentMobileLayout = [
                        ...mobileRenderArrayRef.current,
                    ];

                    const {
                        id: _id,
                        containers: _containers,
                        ...pageFields
                    } = savedPage;

                    const draftPayload = {
                        ...pageFields,
                        draftOfPortalId: livePortalId,
                        containers: [],
                        mobileConfiguration: [],
                    };

                    const createResponse = await createPortalPage(draftPayload);
                    const draftData = createResponse.viewModels[0];
                    draftId = draftData.id;

                    const oldToNewIdMap = new Map();
                    const draftContainerIds = [];
                    const updatedContainersList = [];

                    for (const container of currentNewContainers) {
                        const containerCopy = { ...container };
                        delete containerCopy.temporaryId;
                        delete containerCopy.isNew;
                        delete containerCopy.populationGroups;
                        const oldId = containerCopy.id;
                        delete containerCopy.id;
                        containerCopy.portalId = draftId;
                        if (oldId !== null && oldId !== undefined) {
                            containerCopy.draftOfContainerId = oldId;
                        }

                        const created = await createContainer(containerCopy);
                        const newContainer = created.viewModels[0];
                        const newId = newContainer.id;

                        if (oldId !== null) {
                            oldToNewIdMap.set(oldId, newId);
                        }
                        oldToNewIdMap.set(container.temporaryId, newId);

                        draftContainerIds.push(newId);
                        updatedContainersList.push({
                            ...newContainer,
                            temporaryId: newId,
                        });
                    }

                    const completeMobileLayout = [
                        ...currentMobileLayout,
                        ...currentNewContainers
                            .map(container => container.temporaryId)
                            .filter(
                                containerId =>
                                    !currentMobileLayout.includes(
                                        containerId,
                                    ),
                            ),
                    ];

                    const remappedMobileLayout = completeMobileLayout
                        .map(id => oldToNewIdMap.get(id) ?? null)
                        .filter(id => id !== null);

                    // An empty draft is already created as-is by the POST, so no update is needed.
                    if (draftContainerIds.length > 0) {
                        const {
                            containers: _draftContainers,
                            ...draftWithoutContainers
                        } = draftData;
                        const updatedDraft = {
                            ...draftWithoutContainers,
                            id: draftId,
                            mobileConfiguration: remappedMobileLayout,
                        };
                        await updatePortalPage(updatedDraft);
                    }

                    const draftContainersClean = updatedContainersList.map(
                        c => {
                            const { temporaryId: _tid, ...rest } = c;
                            return rest;
                        },
                    );

                    setContainers(draftContainersClean);
                    setNewContainers(updatedContainersList);
                    setMobileRenderArray(remappedMobileLayout);
                    setMobileConfig(remappedMobileLayout);
                    containersRef.current = draftContainersClean;
                    newContainersRef.current = updatedContainersList;
                    mobileRenderArrayRef.current = remappedMobileLayout;
                    mobileConfigRef.current = remappedMobileLayout;
                    currentContainers = draftContainersClean;
                    currentNewContainers = updatedContainersList;
                    activePortalIdRef.current = draftId;

                    if (onDraftCreated) {
                        onDraftCreated(draftData);
                    }

                    if (isAutoSave) {
                        Toast.success(
                            getText('msg_e5c8c585d4a6'),
                            {},
                            getText('msg_9a3dda75e962'),
                        );
                    } else {
                        Toast.success(
                            getText('msg_e5c8c585d4a6'),
                            {},
                            getText('msg_298d4c6ac749'),
                        );
                    }

                    if (isAutoSave) {
                        await refreshContainers();
                    } else {
                        await loadContainers();
                    }

                    return true;
                } catch (e) {
                    console.error('Error creating draft from Design tab:', e);
                    hasDraftRef.current = false;

                    if (draftId) {
                        try {
                            await deletePortalPage(draftId);
                        } catch (cleanupError) {
                            console.error(
                                'Failed to clean up incomplete draft:',
                                cleanupError,
                            );
                        }
                    }

                    Toast.error(
                        getText('msg_839c21d656e8'),
                    );
                    return false;
                } finally {
                    isSavingRef.current = false;
                    setIsSaving(false);
                    if (!isAutoSave) {
                        setIsLoading(false);
                    }
                }
            }

            if (!isAutoSave) {
                setIsLoading(true); // If save function is triggered manually, display "isLoading" spinner to provide visual context to the user.
            }

            try {
                const newMobileLayout = [...mobileRenderArrayRef.current]; // Copy of the current mobile layout
                const fetchedContainerMap = new Map(
                    currentContainers.map(container => [
                        container.id,
                        container,
                    ]),
                );

                const containersToCreate = [];
                const containersToUpdate = [];

                const currentContainerIds = new Set(
                    currentNewContainers.map(container => container.id),
                );

                for (const container of currentNewContainers) {
                    if (container.id === null) {
                        containersToCreate.push(container);
                    } else {
                        const existingContainer = fetchedContainerMap.get(
                            container.id,
                        );
                        if (existingContainer) {
                            const temporaryContainerCopy = cloneDeep(container);
                            delete temporaryContainerCopy.temporaryId;
                            delete temporaryContainerCopy.populationGroups;
                            delete temporaryContainerCopy.isNew;
                            if (
                                !_.isEqual(
                                    temporaryContainerCopy,
                                    existingContainer,
                                )
                            ) {
                                containersToUpdate.push(container);
                            }
                        }
                    }
                }

                const containersToDelete = currentContainers
                    .filter(container => !currentContainerIds.has(container.id))
                    .map(container => container.id);

                const updatedContainers = [...currentNewContainers];

                for (const container of containersToCreate) {
                    const comparingContainer = { ...container };
                    delete comparingContainer.id;
                    if (savedPage?.agencyId != null) {
                        comparingContainer.agencyId = savedPage.agencyId;
                    }
                    const createdContainer =
                        await createContainer(comparingContainer);

                    const index = updatedContainers.findIndex(
                        item => item.temporaryId === container.temporaryId,
                    );
                    if (index !== -1) {
                        updatedContainers[index] = {
                            ...createdContainer.viewModels[0],
                            temporaryId: container.temporaryId,
                        };
                    }

                    const indexMobile = newMobileLayout.findIndex(
                        id => id === container.temporaryId,
                    );
                    if (indexMobile !== -1) {
                        newMobileLayout[indexMobile] =
                            createdContainer.viewModels[0].id;
                    }
                }

                if (containersToCreate.length > 0) {
                    setNewContainers(updatedContainers);
                }

                for (const container of containersToUpdate) {
                    const updatedContainer = JSON.parse(
                        JSON.stringify(container),
                    );
                    delete updatedContainer.populationGroups;
                    delete updatedContainer.isNew;
                    delete updatedContainer.temporaryId;
                    const updatedFetchContainer =
                        await updateContainer(updatedContainer);

                    const index = updatedContainers.findIndex(
                        item =>
                            item.id === updatedFetchContainer.viewModels[0].id,
                    );
                    if (index !== -1) {
                        updatedContainers[index] = {
                            ...updatedFetchContainer.viewModels[0],
                            temporaryId: updatedFetchContainer.viewModels[0].id,
                        };
                    }
                }

                if (containersToUpdate.length > 0) {
                    setNewContainers(updatedContainers);
                }

                for (const containerId of containersToDelete) {
                    try {
                        await deleteContainer(containerId); // Delete the container
                        const indexMobile = newMobileLayout.findIndex(
                            id => id === containerId,
                        );
                        if (indexMobile !== -1) {
                            newMobileLayout.splice(indexMobile, 1);
                        }
                    } catch (error) {
                        console.error(
                            `Failed to delete container with ID ${containerId}:`,
                            error,
                        ); // Error handling
                    }
                }

                const persistedMobileLayout = mobileConfigRef.current.filter(
                    id => !containersToDelete.includes(id),
                );

                const mobileConfigChanged = !areConfigEqual(
                    newMobileLayout,
                    persistedMobileLayout,
                );

                const shouldUpdateMobileConfig = mobileConfigChanged;

                if (shouldUpdateMobileConfig) {
                    const {
                        containers: _containers,
                        ...pageWithoutContainers
                    } = savedPage;
                    const updatedPage = {
                        ...pageWithoutContainers,
                        id: activePortalIdRef.current,
                        mobileConfiguration: newMobileLayout,
                    };
                    await updatePortalPage(updatedPage);
                }

                // Always reload containers after saving to ensure UI is in sync with backend

                if (isAutoSave) {
                    await refreshContainers();
                } else {
                    await loadContainers();
                }

                if (
                    containersToCreate.length > 0 ||
                    containersToDelete.length > 0 ||
                    containersToUpdate.length > 0 ||
                    shouldUpdateMobileConfig
                ) {
                    const isDraft =
                        savedPage.draftOfPortalId ||
                        activePortalIdRef.current !== portalId;
                    if (isAutoSave) {
                        if (isDraft) {
                            Toast.success(
                                getText('msg_e5c8c585d4a6'),
                                {},
                                getText('msg_63d96157ac3e'),
                            );
                        } else {
                            Toast.success(
                                getText('msg_57c7d11a533c'),
                            );
                        }
                    } else {
                        if (isDraft) {
                            Toast.success(
                                getText('msg_e5c8c585d4a6'),
                                {},
                                getText('msg_d57efcfd7e5c'),
                            );
                        } else {
                            Toast.success(getText('msg_d03a05be4a0f'));
                        }
                    }
                } else {
                    Toast.info(
                        isAutoSave
                            ? 'Auto-save: Your grid is already up to date with the latest changes.'
                            : 'There are no changes to save.',
                    );
                }

                return true;
            } catch (error) {
                console.error('Error saving layout:', error);
                Toast.error(
                    getText('msg_8b2e411ee7fe'),
                );
                return false;
            } finally {
                if (!isAutoSave) {
                    setIsLoading(false);
                }
                isSavingRef.current = false;
                setIsSaving(false);
            }
        };

        function areConfigEqual(arr1, arr2) {
            // Check if both arrays are the same length
            if (arr1.length !== arr2.length) {
                return false;
            }
            // Compare each element in the arrays
            for (let i = 0; i < arr1.length; i++) {
                if (arr1[i] !== arr2[i]) {
                    return false;
                }
            }
            return true; // Return true if all elements are equal and in the same order
        }

        const handleResetMobileLayout = () => {
            // Group containers by their starting row
            const groupedByRow = {};

            newContainers.forEach(container => {
                const row = container.start.row;
                if (!groupedByRow[row]) {
                    groupedByRow[row] = [];
                }
                groupedByRow[row].push(container);
            });

            // Prepare an array to hold the final ordered IDs
            const orderedIds = [];

            // Initialize a variable to track the total number of containers
            const totalContainers = newContainers.length;

            // Sort rows numerically and iterate through each occupied row
            const sortedRows = Object.keys(groupedByRow)
                .map(Number)
                .sort((a, b) => a - b);

            for (const row of sortedRows) {
                const containersInRow = groupedByRow[row].sort(
                    (a, b) => a.start.col - b.start.col,
                );
                orderedIds.push(
                    ...containersInRow.map(container => container.temporaryId),
                );
            }

            // Set the mobile render array with the ordered IDs
            setMobileRenderArray(orderedIds);
        };

        useEffect(() => {
            loadTemplates();
        }, []);

        async function loadTemplates() {
            try {
                const fetchedTemplates = await fetchTemplates();

                let filteredTemplates = fetchedTemplates.viewModels.filter(
                    template =>
                        ![
                            'Text',
                            'Video',
                            'Image',
                            'Location',
                            'Child',
                        ].includes(template.name),
                );

                // silver or better client tier required for asset upload containers
                if (!clientTierMeetsRequirement(TenantTiers.Silver)) {
                    filteredTemplates = filteredTemplates.filter(
                        template =>
                            template.type !== ContainerTypes.AssetUpload,
                    );
                }

                setTemplates(filteredTemplates);
            } catch (error) {
                Toast.error(getText('msg_6f6ffe8437ab'));
                setTemplates([]);
            }
        }

        const handleLaunchModal = existingContainer => {
            if (
                existingContainer.type === ContainerTypes.Multimedia ||
                existingContainer.type === ContainerTypes.Document ||
                existingContainer.type === ContainerTypes.Table ||
                existingContainer.type === ContainerTypes.AssetUpload ||
                existingContainer.type === ContainerTypes.Link
            ) {
                setPropertiesModal(
                    <NewerPropertiesModal
                        item={existingContainer}
                        dismissModal={handleDismissModal}
                        onSave={setSavedItem}
                        portalId={portalId}
                    />,
                );
            } else {
                setPropertiesModal(
                    <PropertiesModal
                        item={existingContainer}
                        dismissModal={handleDismissModal}
                        onSave={setSavedItem}
                        portalId={portalId}
                    />,
                );
            }
        };

        const handleDismissModal = (state, item) => {
            if (!state) {
                showModal({
                    type: ModalTypes.ACTION,
                    title: getText('msg_b78130c92f32'),
                    subtitle:
                        getText('msg_2d48279891a4'),
                    text: getText('msg_0173248a83e9'),
                    onYes: () => {
                        setPropertiesModal(null);
                    },
                    onNo: () => {}, // Don't perform any action, "Cancel" keeps properties open and unsaved
                    noButtonText: getText('msg_77dfd2135f4d'),
                    yesButtonText: getText('msg_a462c99b8b67'),
                });
            } else {
                setPropertiesModal(null);
            }
        };

        const getTemplateLabel = type => {
            switch (type) {
                case ContainerTypes.EnrollmentByAge:
                    return 'Enrollment by Age';
                case ContainerTypes.PremiumByPlanType:
                    return 'Total Premium by Plan Type';
                case ContainerTypes.Video:
                    return 'Video';
                case ContainerTypes.Text:
                    return 'Text';
                case ContainerTypes.Location:
                    return 'Location';
                case ContainerTypes.Image:
                    return 'Image';
                case ContainerTypes.Table:
                    return 'Table';
                case ContainerTypes.Document:
                    return 'Document';
                case ContainerTypes.Multimedia:
                    return 'Multimedia';
                case ContainerTypes.ChildPortal:
                    return 'Child';
                case ContainerTypes.Link:
                    return 'Link';
                case ContainerTypes.AssetUpload:
                    return 'Asset Upload';
                default:
                    return 'Undefined';
            }
        };

        const getTemplateIcon = type => {
            switch (type) {
                case ContainerTypes.EnrollmentByAge:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                        >
                            <path
                                d="M2.80918 8.04236H1.45411C0.877015 8.04236 0.40918 8.61774 0.40918 9.32751V22.7148C0.40918 23.4246 0.877015 24 1.45411 24H2.80918C3.38628 24 3.85411 23.4246 3.85411 22.7148V9.32751C3.85411 8.61774 3.38628 8.04236 2.80918 8.04236Z"
                                fill="url(#paint0_linear_314_2133)"
                            />
                            <path
                                d="M23.3644 5.39038H22.0093C21.4322 5.39038 20.9644 5.96576 20.9644 6.67553V22.7149C20.9644 23.4246 21.4322 24 22.0093 24H23.3644C23.9415 24 24.4093 23.4246 24.4093 22.7149V6.67553C24.4093 5.96576 23.9415 5.39038 23.3644 5.39038Z"
                                fill="url(#paint1_linear_314_2133)"
                            />
                            <path
                                d="M9.91319 11.2711H8.5581C7.98101 11.2711 7.51318 11.8465 7.51318 12.5563V22.7148C7.51318 23.4246 7.98101 24 8.5581 24H9.91319C10.4903 24 10.9581 23.4246 10.9581 22.7148V12.5563C10.9581 11.8465 10.4903 11.2711 9.91319 11.2711Z"
                                fill="url(#paint2_linear_314_2133)"
                            />
                            <path
                                d="M16.7955 0H15.4404C14.8633 0 14.3955 0.575379 14.3955 1.28515V22.7148C14.3955 23.4246 14.8633 24 15.4404 24H16.7955C17.3726 24 17.8404 23.4246 17.8404 22.7148V1.28515C17.8404 0.575379 17.3726 0 16.7955 0Z"
                                fill="url(#paint3_linear_314_2133)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2133"
                                    x1="2.13165"
                                    y1="8.04236"
                                    x2="2.13165"
                                    y2="24"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#1366FF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2133"
                                    x1="22.6868"
                                    y1="5.39038"
                                    x2="22.6868"
                                    y2="24"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#1366FF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint2_linear_314_2133"
                                    x1="9.23565"
                                    y1="11.2711"
                                    x2="9.23565"
                                    y2="24"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#1366FF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint3_linear_314_2133"
                                    x1="16.118"
                                    y1="0"
                                    x2="16.118"
                                    y2="24"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#1366FF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.PremiumByPlanType:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                        >
                            <path
                                d="M15.3842 16.0062C15.3842 16.0057 15.3835 16.0054 15.3831 16.0058C14.373 17.1842 12.8758 17.9355 11.2 17.9355C9.99314 17.9355 8.87388 17.5438 7.96469 16.8824C7.36917 16.4491 6.54693 16.3376 5.95675 16.7781L3.41587 18.6746C2.79721 19.1363 2.66468 20.0196 3.20012 20.5757C5.2327 22.6868 8.08732 24.0001 11.2486 24.0001C14.4014 24.0001 17.1745 22.7326 19.1876 20.6867C19.7289 20.1367 19.6084 19.252 18.9964 18.7818L15.3845 16.0067C15.3843 16.0066 15.3842 16.0064 15.3842 16.0062Z"
                                fill="#17BDBF"
                            />
                            <path
                                d="M5.65791 12.4247C5.65791 11.6465 5.82097 10.9074 6.11343 10.2371C6.4156 9.54442 6.33624 8.69186 5.75068 8.21421L3.7836 6.60961C3.18586 6.12201 2.29658 6.20714 1.89467 6.86556C0.952781 8.40856 0.40918 10.2213 0.40918 12.1605C0.40918 13.945 0.867944 15.6217 1.67435 17.0794C2.04752 17.754 2.93178 17.8768 3.54955 17.4156L5.43395 16.0089C6.00888 15.5797 6.14457 14.7883 5.92329 14.1058C5.75156 13.5761 5.65791 13.0116 5.65791 12.4247Z"
                                fill="url(#paint0_linear_314_2126)"
                            />
                            <path
                                d="M11.1474 6.91362C11.1764 6.91362 11.1999 6.89207 11.1999 6.86307V3.75148C11.1999 2.97995 10.5708 2.34334 9.80798 2.45913C7.9842 2.73599 6.33632 3.54838 5.02915 4.73038C4.45668 5.24803 4.55182 6.13794 5.14982 6.62587L6.40772 7.65222C6.95199 8.09631 7.73342 8.04845 8.33657 7.68838C9.15935 7.19718 10.1196 6.91362 11.1474 6.91362Z"
                                fill="#1366FF"
                            />
                            <path
                                d="M13.2492 0.0790166C12.4826 -0.00827552 11.8545 0.625577 11.8545 1.39711V5.72612C11.8545 6.43103 12.3895 7.00739 13.0521 7.24812C15.168 8.01693 16.6798 10.0429 16.6798 12.4246C16.6798 13.0282 16.5813 13.6073 16.4008 14.1495C16.1755 14.8262 16.3021 15.6133 16.8677 16.0479L20.4136 18.773C21.0251 19.2429 21.9094 19.1319 22.3072 18.4713C23.4113 16.6377 24.0478 14.4905 24.0478 12.1954C24.0496 5.93221 19.3272 0.771137 13.2492 0.0790166Z"
                                fill="url(#paint1_linear_314_2126)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2126"
                                    x1="3.60562"
                                    y1="5.72681"
                                    x2="3.60562"
                                    y2="18.2503"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#F9B77B" />
                                    <stop offset="1" stop-color="#9391FC" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2126"
                                    x1="17.9511"
                                    y1="0.00012207"
                                    x2="17.9511"
                                    y2="19.6236"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#1366FF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Video:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="27"
                            height="20"
                            viewBox="0 0 27 20"
                            fill="none"
                        >
                            <path
                                d="M11.3867 5.15523C11.1311 4.97379 10.795 4.94953 10.5157 5.09236C10.2365 5.2352 10.061 5.52118 10.061 5.83334V14.1667C10.061 14.4788 10.2365 14.7648 10.5157 14.9076C10.795 15.0505 11.1311 15.0262 11.3867 14.8448L17.2556 10.6781C17.4759 10.5217 17.6067 10.2691 17.6067 10C17.6067 9.73088 17.4759 9.47832 17.2556 9.32189L11.3867 5.15523Z"
                                fill="url(#paint0_linear_314_2034)"
                            />
                            <path
                                d="M0 3.33333C0 1.49238 1.50148 0 3.35366 0H23.4756C25.3278 0 26.8293 1.49238 26.8293 3.33333V16.6667C26.8293 18.5076 25.3278 20 23.4756 20H3.35366C1.50148 20 0 18.5076 0 16.6667V3.33333ZM25.1524 3.33333C25.1524 2.41286 24.4017 1.66667 23.4756 1.66667H3.35366C2.42757 1.66667 1.67683 2.41286 1.67683 3.33333V16.6667C1.67683 17.5871 2.42757 18.3333 3.35366 18.3333H23.4756C24.4017 18.3333 25.1524 17.5871 25.1524 16.6667V3.33333Z"
                                fill="url(#paint1_linear_314_2034)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2034"
                                    x1="3.57724"
                                    y1="1.33333"
                                    x2="24.0417"
                                    y2="18.7894"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#1366FF" />
                                    <stop offset="0.795" stop-color="#9391FC" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2034"
                                    x1="3.57724"
                                    y1="1.33333"
                                    x2="24.0417"
                                    y2="18.7894"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#1366FF" />
                                    <stop offset="0.795" stop-color="#9391FC" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Text:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M20.75 19H22.25L19.25 9H17.75L14.75 19H16.25L17 16.5H20L20.75 19ZM17.45 15L18.5 11.5L19.55 15H17.45ZM11.75 19H13.75L8.75 5H6.75L1.75 19H3.75L5.18 15H10.32L11.75 19ZM5.89 13L7.75 7.8L9.61 13H5.89Z"
                                fill="url(#paint0_linear_314_2027)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2027"
                                    x1="12"
                                    y1="5"
                                    x2="12"
                                    y2="19"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#1366FF" />
                                    <stop offset="1" stop-color="#9391FC" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Location:
                    return (
                        <svg
                            style={{ display: 'block', margin: '0 auto' }}
                            xmlns="http://www.w3.org/2000/svg"
                            width="50"
                            height="50"
                            fill="rgba(23, 189, 191, 1)"
                            className="bi bi-geo-alt-fill"
                            viewBox="0 0 16 16"
                        >
                            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" />
                        </svg>
                    );
                case ContainerTypes.Image:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="27"
                            height="20"
                            viewBox="0 0 27 20"
                            fill="none"
                        >
                            <path
                                d="M10.0026 5.83333C10.0026 7.21405 8.88328 8.33333 7.50257 8.33333C6.12186 8.33333 5.00257 7.21405 5.00257 5.83333C5.00257 4.45262 6.12186 3.33333 7.50257 3.33333C8.88328 3.33333 10.0026 4.45262 10.0026 5.83333Z"
                                fill="url(#paint0_linear_314_2031)"
                            />
                            <path
                                d="M2.5 0C1.11929 0 0 1.11929 0 2.5V17.5C0 18.8807 1.11929 20 2.5 20H24.1667C25.5474 20 26.6667 18.8807 26.6667 17.5V2.5C26.6667 1.11929 25.5474 0 24.1667 0H2.5ZM24.1667 1.66667C24.6269 1.66667 25 2.03976 25 2.5V12.4987L18.7086 9.25467C18.3878 9.09425 18.0003 9.15713 17.7466 9.41077L11.5631 15.5944L7.13149 12.64C6.80097 12.4196 6.36087 12.4632 6.07998 12.7441L1.66924 16.6667V17.5659C1.66753 17.5442 1.66667 17.5222 1.66667 17.5V2.5C1.66667 2.03976 2.03976 1.66667 2.5 1.66667H24.1667Z"
                                fill="url(#paint1_linear_314_2031)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2031"
                                    x1="13.3333"
                                    y1="0"
                                    x2="13.3333"
                                    y2="20"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#1366FF" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2031"
                                    x1="13.3333"
                                    y1="0"
                                    x2="13.3333"
                                    y2="20"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#1366FF" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Table:
                    return (
                        <svg
                            width="47"
                            height="33"
                            viewBox="0 0 47 33"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M41.8332 0.458496H5.16658C2.64575 0.458496 0.583252 2.521 0.583252 5.04183V9.62516V13.0627V19.9377V23.3752V27.9585C0.583252 30.4793 2.64575 32.5418 5.16658 32.5418H41.8332C44.3541 32.5418 46.4166 30.4793 46.4166 27.9585V23.3752V19.9377V13.0627V9.62516V5.04183C46.4166 2.521 44.3541 0.458496 41.8332 0.458496ZM17.7708 19.9377V13.0627H29.2291V19.9377H17.7708ZM29.2291 23.3752V29.1043H17.7708V23.3752H29.2291ZM17.7708 9.62516V3.896H29.2291V9.62516H17.7708ZM4.02075 5.04183C4.02075 4.35433 4.47908 3.896 5.16658 3.896H14.3333V9.62516H4.02075V5.04183ZM4.02075 13.0627H14.3333V19.9377H4.02075V13.0627ZM4.02075 27.9585V23.3752H14.3333V29.1043H5.16658C4.47908 29.1043 4.02075 28.646 4.02075 27.9585ZM42.9791 27.9585C42.9791 28.646 42.5207 29.1043 41.8332 29.1043H32.6666V23.3752H42.9791V27.9585ZM42.9791 19.9377H32.6666V13.0627H42.9791V19.9377ZM32.6666 9.62516V3.896H41.8332C42.5207 3.896 42.9791 4.35433 42.9791 5.04183V9.62516H32.6666Z"
                                fill="url(#paint0_linear_78893_18846)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_78893_18846"
                                    x1="23.4999"
                                    y1="0.458496"
                                    x2="23.4999"
                                    y2="32.5418"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="0.5" stop-color="#17BDBF" />
                                    <stop offset="1" stop-color="#F9B77B" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Document:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="19"
                            height="24"
                            viewBox="0 0 19 24"
                            fill="none"
                        >
                            <mask
                                id="mask0_314_2065"
                                style={{ maskType: 'alpha' }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="19"
                                height="24"
                            >
                                <path
                                    d="M16.8685 24H1.48443C0.664602 24 0 23.2325 0 22.2857V1.71429C0 0.767513 0.664602 0 1.48443 0H11.4706L18.3529 7.94805V22.2857C18.3529 23.2325 17.6883 24 16.8685 24Z"
                                    fill="url(#paint0_linear_314_2065)"
                                />
                            </mask>
                            <g mask="url(#mask0_314_2065)">
                                <path
                                    d="M16.8685 24H1.48443C0.664602 24 0 23.2325 0 22.2857V1.71429C0 0.767513 0.664602 0 1.48443 0H11.4706L18.3529 7.94805V22.2857C18.3529 23.2325 17.6883 24 16.8685 24Z"
                                    fill="url(#paint1_linear_314_2065)"
                                />
                                <g filter="url(#filter0_d_314_2065)">
                                    <path
                                        d="M18.3528 7.9322C14.1683 8.6278 10.5508 4.98163 11.2795 0.802752L11.4195 0L12.643 1.22034L15.09 4.0678L16.7214 5.89831L18.3528 7.9322Z"
                                        fill="url(#paint2_linear_314_2065)"
                                    />
                                </g>
                            </g>
                            <defs>
                                <filter
                                    id="filter0_d_314_2065"
                                    x="-11.8145"
                                    y="-24"
                                    width="57.1675"
                                    height="58.0178"
                                    filterUnits="userSpaceOnUse"
                                    color-interpolation-filters="sRGB"
                                >
                                    <feFlood
                                        flood-opacity="0"
                                        result="BackgroundImageFix"
                                    />
                                    <feColorMatrix
                                        in="SourceAlpha"
                                        type="matrix"
                                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                        result="hardAlpha"
                                    />
                                    <feOffset dx="2" dy="1" />
                                    <feGaussianBlur stdDeviation="12.5" />
                                    <feColorMatrix
                                        type="matrix"
                                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.33 0"
                                    />
                                    <feBlend
                                        mode="normal"
                                        in2="BackgroundImageFix"
                                        result="effect1_dropShadow_314_2065"
                                    />
                                    <feBlend
                                        mode="normal"
                                        in="SourceGraphic"
                                        in2="effect1_dropShadow_314_2065"
                                        result="shape"
                                    />
                                </filter>
                                <linearGradient
                                    id="paint0_linear_314_2065"
                                    x1="9.17647"
                                    y1="0"
                                    x2="9.17647"
                                    y2="24"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#FF7979" />
                                    <stop offset="1" stop-color="#E85555" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2065"
                                    x1="9.17647"
                                    y1="0"
                                    x2="9.17647"
                                    y2="24"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint2_linear_314_2065"
                                    x1="14.2744"
                                    y1="-0.40678"
                                    x2="14.2744"
                                    y2="9.35593"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#FFF2E7" />
                                    <stop offset="1" stop-color="#F9B77B" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Multimedia:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="26"
                            height="25"
                            viewBox="0 0 26 25"
                            fill="none"
                        >
                            <path
                                d="M15.0247 11.6826C14.9151 11.5729 14.8785 11.4267 14.9029 11.2683C14.8298 11.2683 14.7567 11.2683 14.6836 11.2683C14.6105 11.2439 14.5495 11.2196 14.5008 11.1708L14.3424 11.0124C14.2327 10.9028 14.1962 10.7566 14.2206 10.5982C14.1474 10.5982 14.0743 10.5982 14.0012 10.5982C13.9281 10.5738 13.8672 10.5494 13.8185 10.5007L13.6601 10.3423C13.6601 10.3423 13.6235 10.3057 13.6113 10.2814L12.5635 12.2918L13.2458 12.9619L15.244 11.9019C15.2197 11.8897 15.2075 11.8775 15.1831 11.8532L15.0247 11.6948V11.6826Z"
                                fill="#F9B77B"
                            />
                            <path
                                d="M12.3929 12.5964L11.8081 13.7174L12.6976 13.23H12.7097L12.9291 13.1203L12.3929 12.5842V12.5964Z"
                                fill="#2F2B43"
                            />
                            <path
                                d="M18.5704 9.13588L15.9873 11.7433C15.9873 11.7433 15.9385 11.7921 15.9142 11.8043C15.8898 11.8286 15.8654 11.8652 15.8289 11.8774L13.587 13.0714L13.4651 13.1324C13.4651 13.1324 13.4408 13.1445 13.4164 13.1689C13.4164 13.1689 13.392 13.1811 13.3798 13.1933C13.3555 13.1933 13.3433 13.2055 13.3189 13.2176C13.3067 13.2176 13.2945 13.2298 13.2824 13.242H17.157V13.8512H12.1492L11.1379 14.3873L11.4181 13.8512H6.03266V13.242H11.7349L12.003 12.7181C12.003 12.7181 12.003 12.7181 12.003 12.7059L12.1614 12.3891C12.1614 12.3891 12.1736 12.3526 12.1979 12.3038C12.2467 12.2185 12.2711 12.1576 12.3198 12.0845L13.6113 9.61107H13.6479C13.6479 9.61107 13.6844 9.55015 13.7088 9.53796L18.5338 4.66421L19.7523 3.4336V2.36137C19.7523 1.06983 18.7044 0.0219727 17.4129 0.0219727H5.70368C4.41214 0.0219727 3.37646 1.06983 3.37646 2.36137V21.7833C3.57141 20.906 4.31466 20.2602 5.27723 20.2115L7.1658 20.1384C8.37205 20.0896 9.45647 21.0156 9.5905 22.2219C9.72452 23.4281 8.84725 24.4394 7.641 24.4882L6.22761 24.5491C6.44693 24.3298 6.61751 24.0739 6.75154 23.7937L7.56789 23.7571C8.37205 23.7206 8.9569 23.0504 8.87161 22.2463C8.78632 21.4421 8.06744 20.8207 7.26328 20.8572L5.3747 20.9304C4.57054 20.9669 3.98569 21.637 4.07098 22.4412C4.09535 22.6971 4.19282 22.9286 4.32685 23.1357C4.26593 23.1479 4.205 23.1601 4.14408 23.1601L3.53486 23.1845C3.47394 23.0626 3.46176 22.9164 3.4252 22.7824C3.49831 23.2088 3.69326 23.5865 3.96131 23.9033H4.22937C5.03354 23.8546 5.61839 23.1845 5.5331 22.3803C5.50873 22.1122 5.41125 21.8685 5.26504 21.6614L6.06921 21.6249C6.17887 21.8442 6.23979 22.0879 6.27635 22.3437C6.386 23.3672 5.7646 24.2567 4.8264 24.5247C5.10664 24.6466 5.42344 24.7197 5.74023 24.7197H13.6844C13.4773 24.5369 13.3555 24.281 13.3555 24.0008V20.9791V20.3699V17.3603C13.3555 16.812 13.7941 16.3612 14.3424 16.3612H19.7888V7.90526L18.5704 9.13588ZM6.09358 2.55632H17.2179V3.16554H6.09358V2.55632ZM6.03266 5.18814H17.157V5.79736H6.03266V5.18814ZM6.03266 7.81997H13.2214V8.42919H6.03266V7.81997ZM6.03266 10.2568H12.8925V10.8661H6.03266V10.2568Z"
                                fill="url(#paint0_linear_314_2037)"
                            />
                            <path
                                d="M17.2176 2.5564H6.09326V3.16561H17.2176V2.5564Z"
                                fill="white"
                            />
                            <path
                                d="M17.1566 5.18811H6.03223V5.79733H17.1566V5.18811Z"
                                fill="white"
                            />
                            <path
                                d="M13.221 7.82007H6.03223V8.42929H13.221V7.82007Z"
                                fill="white"
                            />
                            <path
                                d="M12.892 10.257H6.03223V10.8662H12.892V10.257Z"
                                fill="white"
                            />
                            <path
                                d="M21.7018 21.1376C21.58 22.7946 20.2032 24.1227 18.5461 24.1227C16.889 24.1227 15.5 22.7946 15.3903 21.1376H13.6602V24.0009C13.6602 24.2446 13.7942 24.4639 14.0135 24.5858C14.1353 24.6589 14.245 24.6954 14.3547 24.6954H22.7497C23.1274 24.6954 23.432 24.3786 23.432 24.0009V21.1376H21.7018Z"
                                fill="#F9B77B"
                            />
                            <path
                                d="M13.6602 17.3604V20.2237H15.4513C15.7681 18.7494 17.0474 17.7016 18.5461 17.7016C20.0448 17.7016 21.3241 18.7616 21.6409 20.2237H23.432V17.3604C23.432 16.9705 23.1274 16.6659 22.7497 16.6659H14.3547C13.9769 16.6659 13.6723 16.9827 13.6723 17.3604H13.6602ZM21.8359 17.8112H22.6035V18.591H21.8359V17.8112Z"
                                fill="#F9B77B"
                            />
                            <path
                                d="M13.3555 17.3604V20.37V20.9792V24.0009C13.3555 24.2812 13.4773 24.537 13.6845 24.7198C13.7332 24.7685 13.7941 24.8173 13.855 24.8538C14.0256 24.9513 14.184 25 14.3546 25H22.7496C23.2979 25 23.7366 24.5492 23.7366 24.0009V20.833H23.4198V20.5284H23.7366V17.3604C23.7366 16.8121 23.2979 16.3613 22.7496 16.3613H14.3546C13.8063 16.3613 13.3677 16.8121 13.3677 17.3604H13.3555ZM23.4319 24.0009C23.4319 24.3908 23.1273 24.6954 22.7496 24.6954H14.3546C14.2449 24.6954 14.1353 24.6589 14.0134 24.5858C13.7941 24.4517 13.6601 24.2324 13.6601 24.0009V21.1376H15.3903C15.5121 22.7947 16.8889 24.1228 18.546 24.1228C20.2031 24.1228 21.5799 22.7947 21.7018 21.1376H23.4198V24.0009H23.4319ZM16.7184 20.9061C16.7184 19.8826 17.5347 19.0541 18.546 19.0541C19.5573 19.0541 20.3737 19.8826 20.3737 20.9061C20.3737 21.9296 19.5573 22.7581 18.546 22.7581C17.5347 22.7581 16.7184 21.9296 16.7184 20.9061ZM22.7496 16.6659C23.1273 16.6659 23.4319 16.9827 23.4319 17.3604V20.2238H21.6408C21.324 18.7495 20.0447 17.7016 18.546 17.7016C17.0473 17.7016 15.768 18.7616 15.4512 20.2238H13.6601V17.3604C13.6601 16.9705 13.9647 16.6659 14.3424 16.6659H22.7374H22.7496Z"
                                fill="white"
                            />
                            <path
                                d="M22.6036 17.8112H21.8359V18.5788H22.6036V17.8112ZM22.2989 18.2742H22.1405V18.1158H22.2989V18.2742Z"
                                fill="white"
                            />
                            <path
                                d="M20.3736 20.906C20.3736 19.8825 19.5572 19.054 18.5459 19.054C17.5346 19.054 16.7183 19.8825 16.7183 20.906C16.7183 21.9295 17.5346 22.758 18.5459 22.758C19.5572 22.758 20.3736 21.9295 20.3736 20.906Z"
                                fill="#2F2B43"
                            />
                            <path
                                d="M13.8919 10.0863L14.0503 10.2447C14.0503 10.2447 14.16 10.3056 14.2209 10.2934C14.294 10.2934 14.3671 10.2568 14.428 10.1959L18.5707 6.01668L19.7891 4.78607L21.5437 3.00714L21.0563 2.51978L13.9406 9.69637C13.8188 9.81821 13.8066 9.98879 13.8919 10.0863Z"
                                fill="#F9B77B"
                            />
                            <path
                                d="M14.903 10.9758C14.9761 10.9758 15.0492 10.9392 15.1101 10.8783L18.5705 7.39357L19.7889 6.16295L22.2258 3.68953L21.7384 3.20215L14.6228 10.3787C14.5009 10.5006 14.4887 10.6712 14.574 10.7686L14.7324 10.927C14.7324 10.927 14.8421 10.988 14.903 10.9758Z"
                                fill="#F9B77B"
                            />
                            <path
                                d="M22.9079 4.35968L22.4206 3.87231L15.3049 11.0489C15.1831 11.1708 15.1709 11.3413 15.2562 11.4388L15.4146 11.5972C15.4146 11.5972 15.5242 11.6581 15.5851 11.6459C15.6582 11.6459 15.7314 11.6094 15.7923 11.5485L18.5703 8.74606L19.7887 7.51544L22.9079 4.3475V4.35968Z"
                                fill="#F9B77B"
                            />
                            <path
                                d="M13.745 9.51367C13.745 9.51367 13.7084 9.55022 13.6841 9.57458H13.6475L12.356 12.0724C12.3072 12.1455 12.2829 12.2064 12.2341 12.2917C12.2098 12.3404 12.2098 12.3648 12.1976 12.377L12.0392 12.6938C12.0392 12.6938 12.0392 12.6938 12.0392 12.706L11.7711 13.2299H6.06885V13.8391H11.4543L11.1741 14.3752L12.1854 13.8391H17.1932V13.2299H13.3185C13.3185 13.2299 13.3429 13.2299 13.3551 13.2055C13.3795 13.1933 13.3917 13.1933 13.416 13.1812C13.4282 13.1812 13.4404 13.169 13.4526 13.1568C13.4769 13.1446 13.4769 13.1324 13.5013 13.1202L13.6232 13.0593L15.8651 11.8653C15.8651 11.8653 15.926 11.8165 15.9504 11.7921C15.9747 11.7678 15.9991 11.7556 16.0235 11.7312L18.6066 9.12377L19.825 7.89314L25.113 2.5564C25.4785 2.19086 25.5395 1.64256 25.247 1.36232L24.0652 0.180438C23.7727 -0.111987 23.2366 -0.0388773 22.8711 0.326654L19.825 3.39711L18.6066 4.62773L13.7815 9.50148L13.745 9.51367ZM12.7093 13.2299H12.6971L11.7955 13.7051L12.3804 12.5841L12.9165 13.1081L12.7093 13.2177V13.2299ZM13.2454 12.9497L12.5631 12.2673L13.611 10.2691C13.611 10.2691 13.6353 10.3056 13.6597 10.3178L13.8181 10.4762C13.8181 10.4762 13.9278 10.5615 14.0009 10.5737C14.074 10.5981 14.1471 10.5981 14.2202 10.5737C14.1958 10.7321 14.2202 10.8783 14.342 10.988L14.5004 11.1464C14.5004 11.1464 14.6101 11.2317 14.6832 11.2438C14.7563 11.2682 14.8294 11.2682 14.9025 11.2438C14.8781 11.4022 14.9025 11.5485 15.0244 11.6581L15.1827 11.8165C15.1827 11.8165 15.2193 11.8531 15.2437 11.8653L13.2454 12.9253V12.9497ZM14.6223 10.3788L21.7379 3.20217L22.2375 3.68954L19.7884 6.16297L18.57 7.39359L15.1096 10.8783C15.0487 10.9392 14.9756 10.9758 14.9025 10.9758C14.8416 10.9758 14.7685 10.9758 14.7319 10.927L14.5735 10.7687C14.4761 10.6712 14.5004 10.5006 14.6223 10.3788ZM19.7884 7.52762L18.57 8.75823L15.792 11.5606C15.731 11.6216 15.6579 11.6581 15.5848 11.6581C15.5239 11.6581 15.4508 11.6581 15.4143 11.6094L15.2559 11.451C15.1584 11.3535 15.1827 11.1829 15.3046 11.0611L22.4203 3.88449L22.9198 4.37186L19.7884 7.52762ZM23.0051 0.533786C23.1879 0.35102 23.4194 0.290103 23.6143 0.338841C23.6753 0.351025 23.7362 0.387571 23.7849 0.436309L24.9668 1.61819C25.1496 1.80096 25.113 2.15431 24.8815 2.38581L23.1513 4.12818L21.2871 2.27615L23.0173 0.533786H23.0051ZM21.0556 2.51984L21.5552 3.00721L19.7884 4.78614L18.57 6.01675L14.4273 10.196C14.3664 10.2569 14.2933 10.2935 14.2202 10.2935C14.1593 10.2935 14.0862 10.2935 14.0496 10.2447L13.8912 10.0863C13.7937 9.98886 13.8181 9.81828 13.9399 9.69643L21.0556 2.51984Z"
                                fill="white"
                            />
                            <path
                                d="M23.1518 4.11595L24.8698 2.37359C25.1013 2.1299 25.15 1.78874 24.9551 1.60598L23.7732 0.42409C23.7732 0.42409 23.6757 0.350982 23.6148 0.326613C23.432 0.265691 23.1883 0.326618 23.0056 0.521568L21.2754 2.26392L23.1396 4.11595H23.1518Z"
                                fill="#17BDBF"
                            />
                            <path
                                d="M4.16868 23.1602C4.22961 23.1602 4.29053 23.148 4.35145 23.1358C4.21742 22.9287 4.11995 22.6971 4.09558 22.4413C4.01029 21.6371 4.59514 20.967 5.39931 20.9304L7.28789 20.8573C8.09206 20.8208 8.81093 21.4422 8.89622 22.2463C8.98151 23.0505 8.39667 23.7206 7.5925 23.7572L6.77614 23.7937C6.64211 24.074 6.47153 24.3299 6.25221 24.5492L7.6656 24.4883C8.87185 24.4395 9.74913 23.4282 9.6151 22.222C9.48107 21.0157 8.39666 20.0897 7.19041 20.1384L5.30183 20.2115C4.33927 20.2481 3.59602 20.906 3.40107 21.7833C3.35233 22.0026 3.32796 22.2341 3.35233 22.4778C3.35233 22.5875 3.41325 22.685 3.43762 22.7946C3.47418 22.9287 3.49855 23.0749 3.54728 23.1967L4.15651 23.1723L4.16868 23.1602Z"
                                fill="url(#paint1_linear_314_2037)"
                            />
                            <path
                                d="M6.27629 22.3438C6.25192 22.0879 6.17882 21.8442 6.06916 21.6249L5.26499 21.6615C5.4112 21.8686 5.50868 22.1123 5.53304 22.3804C5.61833 23.1845 5.03349 23.8547 4.22932 23.8912H3.96126L2.34074 23.9643C1.53657 24.0009 0.817696 23.3795 0.732405 22.5753C0.647115 21.7711 1.23196 21.101 2.03613 21.0644L2.85248 21.0279C2.98651 20.7476 3.15709 20.4918 3.37641 20.2725L1.96302 20.3334C0.756772 20.3821 -0.1205 21.3934 0.0135276 22.5997C0.147556 23.8059 1.23196 24.7319 2.43821 24.6832L4.32679 24.6101C4.49738 24.6101 4.66795 24.5735 4.82635 24.5248C5.76454 24.2567 6.38595 23.3673 6.27629 22.3438Z"
                                fill="url(#paint2_linear_314_2037)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2037"
                                    x1="11.5826"
                                    y1="0.0219727"
                                    x2="11.5826"
                                    y2="24.7197"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#F9B77B" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2037"
                                    x1="6.4857"
                                    y1="20.1366"
                                    x2="6.4857"
                                    y2="24.5492"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint2_linear_314_2037"
                                    x1="3.1444"
                                    y1="20.2725"
                                    x2="3.1444"
                                    y2="24.685"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.ChildPortal:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="21"
                            viewBox="0 0 25 21"
                            fill="none"
                        >
                            <path
                                d="M4.15918 4.5C4.57339 4.5 4.90918 4.16421 4.90918 3.75C4.90918 3.33579 4.57339 3 4.15918 3C3.74497 3 3.40918 3.33579 3.40918 3.75C3.40918 4.16421 3.74497 4.5 4.15918 4.5Z"
                                fill="url(#paint0_linear_314_2092)"
                            />
                            <path
                                d="M6.40918 4.5C6.82339 4.5 7.15918 4.16421 7.15918 3.75C7.15918 3.33579 6.82339 3 6.40918 3C5.99497 3 5.65918 3.33579 5.65918 3.75C5.65918 4.16421 5.99497 4.5 6.40918 4.5Z"
                                fill="url(#paint1_linear_314_2092)"
                            />
                            <path
                                d="M9.40918 3.75C9.40918 4.16421 9.07339 4.5 8.65918 4.5C8.24497 4.5 7.90918 4.16421 7.90918 3.75C7.90918 3.33579 8.24497 3 8.65918 3C9.07339 3 9.40918 3.33579 9.40918 3.75Z"
                                fill="url(#paint2_linear_314_2092)"
                            />
                            <path
                                d="M0.40918 3C0.40918 1.34315 1.75233 0 3.40918 0H19.9092C21.566 0 22.9092 1.34315 22.9092 3V9C22.9092 9.41421 22.5734 9.75 22.1592 9.75C21.745 9.75 21.4092 9.41421 21.4092 9V7.5H1.90918V15C1.90918 15.8284 2.58075 16.5 3.40918 16.5H11.6592C12.0734 16.5 12.4092 16.8358 12.4092 17.25C12.4092 17.6642 12.0734 18 11.6592 18H3.40918C1.75233 18 0.40918 16.6569 0.40918 15V3ZM1.90918 6H21.4092V3C21.4092 2.17157 20.7376 1.5 19.9092 1.5H3.40918C2.58075 1.5 1.90918 2.17157 1.90918 3V6Z"
                                fill="url(#paint3_linear_314_2092)"
                            />
                            <path
                                d="M24.4092 15.75C24.4092 18.6495 22.0587 21 19.1592 21C16.2597 21 13.9092 18.6495 13.9092 15.75C13.9092 12.8505 16.2597 10.5 19.1592 10.5C22.0587 10.5 24.4092 12.8505 24.4092 15.75ZM19.1592 12.75C18.745 12.75 18.4092 13.0858 18.4092 13.5V15H16.9092C16.495 15 16.1592 15.3358 16.1592 15.75C16.1592 16.1642 16.495 16.5 16.9092 16.5H18.4092V18C18.4092 18.4142 18.745 18.75 19.1592 18.75C19.5734 18.75 19.9092 18.4142 19.9092 18V16.5H21.4092C21.8234 16.5 22.1592 16.1642 22.1592 15.75C22.1592 15.3358 21.8234 15 21.4092 15H19.9092V13.5C19.9092 13.0858 19.5734 12.75 19.1592 12.75Z"
                                fill="url(#paint4_linear_314_2092)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2092"
                                    x1="12.4092"
                                    y1="0"
                                    x2="12.4092"
                                    y2="21"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2092"
                                    x1="12.4092"
                                    y1="0"
                                    x2="12.4092"
                                    y2="21"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint2_linear_314_2092"
                                    x1="12.4092"
                                    y1="0"
                                    x2="12.4092"
                                    y2="21"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint3_linear_314_2092"
                                    x1="12.4092"
                                    y1="0"
                                    x2="12.4092"
                                    y2="21"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint4_linear_314_2092"
                                    x1="12.4092"
                                    y1="0"
                                    x2="12.4092"
                                    y2="21"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.Link:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                        >
                            <g clip-path="url(#clip0_314_2086)">
                                <path
                                    d="M15.7145 11.7533C15.6814 11.1364 15.5301 10.5439 15.2886 10.0063L13.3538 10.014C13.6843 10.526 13.8941 11.121 13.926 11.7671C14.0508 13.7068 12.5797 15.2579 10.6448 15.2657L10.0011 15.2391L6.10217 15.2534C4.16734 15.2612 2.50278 13.6974 2.37794 11.7577C2.25311 9.81805 3.72427 8.2669 5.65911 8.25911L7.62319 8.25254C7.97293 7.59287 8.40803 6.99539 8.95654 6.49051L5.55626 6.49621C2.65461 6.49327 0.447265 8.83464 0.649146 11.7448C0.851028 14.6549 3.3631 16.9866 6.26474 16.9895L10.8074 17.0018C11.2171 17.0187 11.6303 16.9479 12.0156 16.8467C14.2953 16.2963 15.8761 14.2221 15.7145 11.7533Z"
                                    fill="url(#paint0_linear_314_2086)"
                                />
                                <path
                                    d="M9.14703 11.6252C9.18536 12.2418 9.34171 12.833 9.58781 13.3685L11.5225 13.3443C11.1876 12.8352 10.9727 12.242 10.9354 11.5962C10.794 9.65762 12.2519 8.09402 14.1866 8.06975L14.8306 8.09093L18.7292 8.04336C20.6639 8.0191 22.3417 9.56866 22.4831 11.5072C22.6244 13.4458 21.1665 15.0094 19.2318 15.0336L17.2679 15.0569C16.9238 15.7196 16.4938 16.3207 15.9496 16.8303L19.3497 16.7956C22.2513 16.7738 24.4386 14.4138 24.2119 11.5055C23.9853 8.59715 21.4534 6.28694 18.5519 6.30871L14.0093 6.33514C13.5995 6.32166 13.1868 6.39597 12.8025 6.50053C10.5275 7.07034 8.96448 9.15792 9.14703 11.6252Z"
                                    fill="url(#paint1_linear_314_2086)"
                                />
                            </g>
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2086"
                                    x1="8.39938"
                                    y1="6.46743"
                                    x2="7.96047"
                                    y2="17.0642"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="1" stop-color="#17BDBF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2086"
                                    x1="16.5069"
                                    y1="16.8486"
                                    x2="16.8556"
                                    y2="6.24849"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#17BDBF" />
                                    <stop offset="0.995" stop-color="#9391FC" />
                                </linearGradient>
                                <clipPath id="clip0_314_2086">
                                    <rect
                                        width="24"
                                        height="24"
                                        fill="white"
                                        transform="translate(0.40918)"
                                    />
                                </clipPath>
                            </defs>
                        </svg>
                    );
                case ContainerTypes.AssetUpload:
                    return (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M12 0C9.86038 0 7.96764 0.840683 6.60864 2.01273C5.45978 3.00355 4.62719 4.29255 4.41334 5.5875C1.89879 6.14267 0 8.33267 0 10.9773C0 14.049 2.56131 16.5 5.67188 16.5H11.25V8.56066L8.03033 11.7803C7.73744 12.0732 7.26256 12.0732 6.96967 11.7803C6.67678 11.4874 6.67678 11.0126 6.96967 10.7197L11.4697 6.21967C11.7626 5.92678 12.2374 5.92678 12.5303 6.21967L17.0303 10.7197C17.3232 11.0126 17.3232 11.4874 17.0303 11.7803C16.7374 12.0732 16.2626 12.0732 15.9697 11.7803L12.75 8.56066V16.5H19.0312C21.7535 16.5 24 14.3542 24 11.6591C24 9.20484 22.1371 7.20616 19.7485 6.86847C19.3849 2.99916 16.0354 0 12 0Z"
                                fill="url(#paint0_linear_314_2074)"
                            />
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M11.25 21.75V16.5H12.75V21.75C12.75 22.1642 12.4142 22.5 12 22.5C11.5858 22.5 11.25 22.1642 11.25 21.75Z"
                                fill="url(#paint1_linear_314_2074)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_314_2074"
                                    x1="12"
                                    y1="0"
                                    x2="12"
                                    y2="22.5"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="0.98" stop-color="#1366FF" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_314_2074"
                                    x1="12"
                                    y1="0"
                                    x2="12"
                                    y2="22.5"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#9391FC" />
                                    <stop offset="0.98" stop-color="#1366FF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    );
                default:
                    return <svg></svg>;
            }
        };

        // *******************************************************
        // MOBILE LOGIC
        // *******************************************************

        const [mobileRenderArray, setMobileRenderArray] = useState([]);

        // Function to find the best position for the dragged container
        const findBestPosition = (newContainer, mobileArray) => {
            const currentRow = newContainer.start.row;
            const currentCol = newContainer.start.col;

            // Check for left neighbors
            for (let i = mobileArray.length - 1; i >= 0; i--) {
                const leftContainerId = mobileArray[i];
                const leftContainer = newContainers.find(
                    container => container.temporaryId === leftContainerId,
                );
                if (
                    leftContainer &&
                    leftContainer.end.row >= currentRow &&
                    leftContainer.start.row <= currentRow
                ) {
                    // Found a left neighbor in the same row
                    return i + 1; // Place after the left neighbor
                }
            }

            // Check for right neighbors
            for (let i = 0; i < mobileArray.length; i++) {
                const rightContainerId = mobileArray[i];
                const rightContainer = newContainers.find(
                    container => container.temporaryId === rightContainerId,
                );
                if (
                    rightContainer &&
                    rightContainer.start.row <= currentRow &&
                    rightContainer.end.row >= currentRow
                ) {
                    // Found a right neighbor in the same row
                    return i - 1; // Place before the right neighbor
                }
            }

            // Check for top neighbors
            for (let i = mobileArray.length - 1; i >= 0; i--) {
                const topContainerId = mobileArray[i];
                const topContainer = newContainers.find(
                    container => container.temporaryId === topContainerId,
                );
                if (topContainer && topContainer.end.row < currentRow) {
                    // Found a top neighbor
                    // Now find the most right container in the same row as the top neighbor
                    const rightNeighborIndex = mobileArray.findIndex(id => {
                        const rightContainer = newContainers.find(
                            container => container.temporaryId === id,
                        );
                        return (
                            rightContainer &&
                            rightContainer.start.row === topContainer.start.row
                        );
                    });

                    if (rightNeighborIndex !== -1) {
                        return rightNeighborIndex + 1; // Place after the most right neighbor
                    } else {
                        return i + 1; // Place after the top neighbor
                    }
                }
            }

            // Check for bottom neighbors
            for (let i = 0; i < mobileArray.length; i++) {
                const bottomContainerId = mobileArray[i];
                const bottomContainer = newContainers.find(
                    container => container.temporaryId === bottomContainerId,
                );
                if (bottomContainer && bottomContainer.start.row > currentRow) {
                    // Found a bottom neighbor
                    // Now find the left-most neighbor in the same row as the bottom neighbor
                    const leftNeighborIndex = mobileArray.findIndex(id => {
                        const leftContainer = newContainers.find(
                            container => container.temporaryId === id,
                        );
                        return (
                            leftContainer &&
                            leftContainer.start.row ===
                                bottomContainer.start.row
                        );
                    });

                    if (leftNeighborIndex !== -1) {
                        return leftNeighborIndex; // Place before the left-most neighbor
                    } else {
                        return i; // Place before the bottom neighbor
                    }
                }
            }

            // If no suitable position is found, place at the end
            return mobileArray.length;
        };

        // *******************************************************
        // GRID LOGIC: FORMER FREEFORMGRID.JS
        // *******************************************************

        const COLUMN_COUNT = 96;
        const GAP = 1;
        const [rowCount, setRowCount] = useState(120); // Initial value

        // Function to calculate the highest row value with a minimum of 120
        const calculateRowCount = containers => {
            if (containers.length === 0) return 120; // Return 120 if there are no containers
            const highestRow =
                containers.reduce((max, container) => {
                    return Math.max(max, container.end.row);
                }, 0) + 1; // +1 because end is exclusive
            return Math.max(highestRow, 120); // Ensure a minimum of 120
        };

        // Use selectedResolution directly as GRID_WIDTH
        const GRID_WIDTH = selectedResolution; // Assuming selectedResolution is a number representing width

        const [rawBlocks, setRawBlocks] = useState([]);

        // Square cells: the cell height always matches the computed column width
        const cellSize = useMemo(() => {
            const totalGapWidth = GAP * (COLUMN_COUNT - 1);
            return (GRID_WIDTH - totalGapWidth) / COLUMN_COUNT;
        }, [COLUMN_COUNT, GAP, GRID_WIDTH]);

        const items = useMemo(
            () =>
                Array.from(
                    { length: rowCount * COLUMN_COUNT },
                    (_unused, index) => index + 1,
                ),
            [COLUMN_COUNT, rowCount],
        );

        const gridHeight = useMemo(
            () => rowCount * (cellSize + GAP) - GAP,
            [cellSize, GAP, rowCount],
        );

        const contentBlocks = useMemo(
            () =>
                rawBlocks.map(block => {
                    const width =
                        (block.end.col - block.start.col + 1) * cellSize +
                        (block.end.col - block.start.col) * GAP;
                    const height =
                        (block.end.row - block.start.row + 1) * cellSize +
                        (block.end.row - block.start.row) * GAP;

                    return { ...block, width, height };
                }),
            [rawBlocks, cellSize, GAP],
        );

        const contentDivRefs = useRef({});
        const mediaAspectRatioPromisesRef = useRef(new Map());
        const mediaAspectRatiosRef = useRef(new Map());
        const preserveWidthOnAutoSizeRef = useRef(new Set());

        // Populate rawBlocks from containers only once when the component mounts
        useEffect(() => {
            setRawBlocks(newContainers);
            saveGrid(newContainers);
            // Calculate and set the row count based on the highest row value
            const highestRowCount = calculateRowCount(newContainers);
            setRowCount(highestRowCount);

            const ids = getOverlappingContainerIds(newContainers);
            setOverlappingIds(ids);
            if (onOverlapChange) onOverlapChange(ids.size > 0);
        }, [newContainers]);

        // Sync overlay DOM visibility after React has committed the content blocks to the DOM
        useEffect(() => {
            newContainers.forEach(c => {
                toggleHighlight(
                    c.temporaryId,
                    overlappingIds.has(c.temporaryId),
                );
            });
        }, [overlappingIds, contentBlocks]);

        const checkAndUpdateHeight = blockId => {
            const contentDiv = contentDivRefs.current[blockId];
            if (contentDiv) {
                return contentDiv.scrollHeight; // Return the height of the content
            }
            return 0; // Return 0 if the content div is not found
        };

        // ------------------------------------------------------------------
        // ADD CONTAINER
        // ------------------------------------------------------------------

        //TODO: TEMPORARY FUNCTION TO GENERATE RANDOM IDs FOR NEW CONTAINERS
        const generateUniqueId = () => {
            let id;
            const existingIds = newContainers.map(
                container => container.temporaryId,
            );

            do {
                // Generate a random number, you can adjust the range as needed
                id = Math.floor(Math.random() * 100); // Generates a number between 0 and 100
            } while (existingIds.includes(id)); // Check for uniqueness

            return id;
        };

        const launchContainerCreation = template => {
            setIsAddContainerOpen(false);
            const newContainer = {
                ...template,
                templateStatus: 1,
                containerType: template.type,
                displayTitle: false,
            };

            if (
                template.type === ContainerTypes.Multimedia ||
                template.type === ContainerTypes.Document ||
                template.type === ContainerTypes.Table ||
                template.type === ContainerTypes.AssetUpload ||
                template.type === ContainerTypes.Link
            ) {
                setPropertiesModal(
                    <NewerPropertiesModal
                        item={newContainer}
                        dismissModal={handleDismissModal}
                        onSave={setSavedItem}
                        portalId={portalId}
                    />,
                );
            } else {
                setPropertiesModal(
                    <PropertiesModal
                        item={newContainer}
                        dismissModal={handleDismissModal}
                        onSave={setSavedItem}
                        portalId={portalId}
                    />,
                );
            }
        };

        useEffect(() => {
            if (savedItem !== undefined && savedItem !== null) {
                if (savedItem.temporaryId === undefined) {
                    //NEW CONTAINER

                    // Figure out placement in grid
                    const highestRow = newContainers.reduce(
                        (max, container) => {
                            return Math.max(max, container.end.row);
                        },
                        0,
                    );

                    const startRow = highestRow > 0 ? highestRow + 1 : 0; // Start in the next row or start on row 0 if grid is empty
                    const startCol = 0; // Start in column 0

                    // Get minimum size for the new container
                    const { minCols, minRows } =
                        getMinimumSizeInGrid(savedItem);

                    // Set initial end coordinates based on minimum size
                    const endRow = startRow + minRows - 1; // End row based on minimum rows
                    const endCol = startCol + minCols - 1; // End column based on minimum columns

                    const temporaryId = generateUniqueId();
                    const newContainer = {
                        ...savedItem,
                        id: null,
                        temporaryId: temporaryId,
                        portalId: savedPage.id,
                        isTemplate: false,
                        name: getTemplateLabel(savedItem.type),
                        order: 0,
                        row: 0,
                        cell: 0,
                        start: { col: startCol, row: startRow },
                        end: { col: endCol, row: endRow },
                        isNew: true,
                    };

                    setNewContainers(prevContainers => [
                        ...prevContainers,
                        newContainer,
                    ]);
                    setMobileRenderArray(prevArray => [
                        ...prevArray,
                        newContainer.temporaryId,
                    ]);
                } else {
                    // Preserve the existing container's id
                    const existingContainerId = newContainers.find(
                        container =>
                            container.temporaryId === savedItem.temporaryId,
                    )?.id;
                    preserveWidthOnAutoSizeRef.current.add(
                        savedItem.temporaryId,
                    );

                    const updatedContainer = {
                        ...savedItem,
                        id: existingContainerId, // Use the existing container's id
                        order: 0,
                        isNew: true, // Set isNew to true for the updated container so it re-checks the dimensions
                    };

                    setNewContainers(prevContainers =>
                        prevContainers.map(container =>
                            container.temporaryId ===
                            updatedContainer.temporaryId
                                ? updatedContainer
                                : container,
                        ),
                    );
                }

                setSavedItem(null);
            }
        }, [savedItem]);

        const getMediaAspectRatio = mp => {
            const hasImages = Array.isArray(mp.images) && mp.images.length > 0;
            const hasVideo = mp.video && mp.video !== '';

            if (hasImages) {
                const imageUrl = mp.images[0];
                const cachedAspectRatio =
                    mediaAspectRatiosRef.current.get(imageUrl);
                if (cachedAspectRatio) {
                    return Promise.resolve(cachedAspectRatio);
                }

                const cachedPromise =
                    mediaAspectRatioPromisesRef.current.get(imageUrl);
                if (cachedPromise) {
                    return cachedPromise;
                }

                const aspectRatioPromise = new Promise(resolve => {
                    const img = new window.Image();
                    let isSettled = false;
                    const timeoutId = window.setTimeout(() => {
                        finishLoading(null);
                    }, 15000);

                    function finishLoading(aspectRatio) {
                        if (isSettled) {
                            return;
                        }
                        isSettled = true;
                        window.clearTimeout(timeoutId);
                        resolve(aspectRatio);
                    }

                    img.onload = () => {
                        if (img.naturalWidth && img.naturalHeight) {
                            finishLoading(img.naturalHeight / img.naturalWidth);
                            return;
                        }
                        finishLoading(null);
                    };
                    img.onerror = () => finishLoading(null);
                    img.src = imageUrl;
                }).then(aspectRatio => {
                    mediaAspectRatioPromisesRef.current.delete(imageUrl);
                    if (aspectRatio !== null) {
                        mediaAspectRatiosRef.current.set(imageUrl, aspectRatio);
                    }
                    return aspectRatio;
                });
                mediaAspectRatioPromisesRef.current.set(
                    imageUrl,
                    aspectRatioPromise,
                );
                return aspectRatioPromise;
            }

            if (hasVideo) {
                return Promise.resolve(9 / 16);
            }

            return Promise.resolve(null);
        };

        useEffect(() => {
            newContainers.forEach(container => {
                const imageUrl = getRatioLockedImageUrl(container);
                if (imageUrl) {
                    getMediaAspectRatio({ images: [imageUrl] });
                }
            });
        }, [newContainers]);

        useEffect(() => {
            // Only run for new containers
            const newContainerIds = newContainers
                .filter(c => c.isNew)
                .map(c => c.temporaryId);
            if (newContainerIds.length === 0) return;

            // Debounce update
            let debounceTimer = null;
            const handleResize = async () => {
                if (debounceTimer) clearTimeout(debounceTimer);
                debounceTimer = setTimeout(async () => {
                    for (const container of newContainers) {
                        if (!container.isNew) continue;
                        const isPlacedContainerUpdate =
                            preserveWidthOnAutoSizeRef.current.delete(
                                container.temporaryId,
                            );
                        const { minCols, minRows } =
                            getMinimumSizeInGrid(container);

                        const isVideoOnlyMediaOnly =
                            isMediaOnlyBlock(container) &&
                            container.multimediaPart?.video &&
                            container.multimediaPart.video !== '' &&
                            (!Array.isArray(container.multimediaPart?.images) ||
                                container.multimediaPart.images.length === 0);
                        const isSingleImageMediaOnly =
                            isSingleImageWithoutBodyContent(container) ||
                            isVideoOnlyMediaOnly;

                        if (isSingleImageMediaOnly) {
                            const imageUrl = getRatioLockedImageUrl(container);
                            const mediaSource = imageUrl
                                ? { images: [imageUrl] }
                                : container.multimediaPart;
                            const mediaRatio =
                                await getMediaAspectRatio(mediaSource);

                            const MEDIA_ONLY_MIN_COLS = 7;
                            const GRID_COLUMN_COUNT = 96;
                            const currentCols =
                                container.end.col - container.start.col + 1;
                            const currentRows =
                                container.end.row - container.start.row + 1;
                            const shouldPreserveCurrentWidth =
                                isPlacedContainerUpdate ||
                                (container.id !== null &&
                                    container.id !== undefined);

                            let finalCols = shouldPreserveCurrentWidth
                                ? currentCols
                                : Math.max(MEDIA_ONLY_MIN_COLS, minCols);
                            let finalRows = shouldPreserveCurrentWidth
                                ? currentRows
                                : minRows;

                            if (mediaRatio !== null) {
                                const titleAdditionalRows =
                                    getTitleAdditionalRows(container, cellSize);
                                const minimumImageRows = Math.max(
                                    minRows - titleAdditionalRows,
                                    1,
                                );
                                const derivedCols = Math.ceil(
                                    minimumImageRows / mediaRatio,
                                );
                                if (!shouldPreserveCurrentWidth) {
                                    const maxCols =
                                        GRID_COLUMN_COUNT - container.start.col;
                                    finalCols = Math.min(derivedCols, maxCols);
                                    finalCols = Math.max(
                                        finalCols,
                                        MEDIA_ONLY_MIN_COLS,
                                        minCols,
                                    );
                                }
                                finalRows = Math.max(
                                    Math.round(finalCols * mediaRatio) +
                                        titleAdditionalRows,
                                    minRows,
                                );
                            }

                            const updatedContainer = {
                                ...container,
                                end: {
                                    col: container.start.col + finalCols - 1,
                                    row: container.start.row + finalRows - 1,
                                },
                                isNew: false,
                            };
                            setNewContainers(prevContainers =>
                                prevContainers.map(c =>
                                    c.temporaryId === container.temporaryId
                                        ? updatedContainer
                                        : c,
                                ),
                            );
                            continue;
                        }

                        const height = checkAndUpdateHeight(
                            container.temporaryId,
                        );
                        const totalHeight = height;
                        const rowsNeeded = Math.ceil(
                            totalHeight / (cellSize + GAP),
                        );
                        let numberRows =
                            rowsNeeded < minRows ? minRows : rowsNeeded;
                        const updatedContainer = {
                            ...container,
                            end: {
                                ...container.end,
                                row: container.start.row + numberRows - 1,
                            },
                            isNew: false,
                        };
                        setNewContainers(prevContainers =>
                            prevContainers.map(c =>
                                c.temporaryId === container.temporaryId
                                    ? updatedContainer
                                    : c,
                            ),
                        );
                    }
                }, 300); // Debounce to 300ms
            };

            // Attach ResizeObservers to each new container's content div
            const observers = [];
            for (const id of newContainerIds) {
                const el = contentDivRefs.current[id];
                if (el) {
                    const observer = new window.ResizeObserver(() => {
                        handleResize();
                    });
                    observer.observe(el);
                    observers.push(observer);
                }
            }

            // Fallback: also run handleResize once after mount
            handleResize();

            return () => {
                observers.forEach(o => o.disconnect());
                if (debounceTimer) clearTimeout(debounceTimer);
            };
        }, [contentBlocks, newContainers, cellSize, GAP]);

        // Function to update the container's coordinates
        const handleUpdateContainer = (containerId, updatedCoordinates) => {
            const newEndRow = updatedCoordinates.end?.row;
            if (newEndRow !== undefined) {
                setRowCount(currentRowCount =>
                    Math.max(currentRowCount, newEndRow + 1),
                );
            }

            setNewContainers(prevContainers =>
                prevContainers.map(container => {
                    if (container.temporaryId === containerId) {
                        return {
                            ...container,
                            start: updatedCoordinates.start || container.start,
                            end: updatedCoordinates.end || container.end,
                        };
                    }
                    return container;
                }),
            );
        };

        // ----------------------------------------
        // DRAG LOGIC
        // ----------------------------------------
        const handleMouseDown = (e, block) => {
            e.stopPropagation();
            const originalBlockPosition = { ...block };
            let latestBlock = block;

            const columnWidth = getColumnWidth();
            const rowHeight = columnWidth; // squares

            const blockElement = document.getElementById(
                `block-${block.temporaryId}`,
            );
            const { left, top } = blockElement.getBoundingClientRect();
            const initialX = e.clientX;
            const initialY = e.clientY;

            // Calculate the initial position of the block in terms of grid coordinates
            const initialCol = block.start.col;
            const initialRow = block.start.row;

            const onMouseMove = moveEvent => {
                if (activeBlock) {
                    setActiveBlock(null);
                }
                // Calculate the difference in cursor position
                const dx = moveEvent.clientX - initialX; // Difference in X
                const dy = moveEvent.clientY - initialY; // Difference in Y

                // Calculate new column and row based on the drag distance
                const newCol = Math.max(
                    0,
                    Math.min(
                        COLUMN_COUNT - 1 - (block.end.col - block.start.col),
                        initialCol + Math.floor(dx / (columnWidth + GAP)),
                    ),
                );
                const newRow = Math.max(
                    0,
                    initialRow + Math.floor(dy / (rowHeight + GAP)),
                );

                const newBlock = {
                    ...block,
                    start: { col: newCol, row: newRow },
                    end: {
                        col: newCol + (block.end.col - block.start.col),
                        row: newRow + (block.end.row - block.start.row),
                    },
                };
                latestBlock = newBlock;

                setRawBlocks(prevBlocks => {
                    const updatedBlocks = prevBlocks.map(b =>
                        b.temporaryId === block.temporaryId ? newBlock : b,
                    );
                    const overlap = isOverlapping(newBlock, updatedBlocks);
                    toggleHighlight(block.temporaryId, overlap);
                    return updatedBlocks;
                });
            };

            const onMouseUp = () => {
                handleUpdateContainer(latestBlock.temporaryId, {
                    start: {
                        col: latestBlock.start.col,
                        row: latestBlock.start.row,
                    },
                    end: {
                        col: latestBlock.end.col,
                        row: latestBlock.end.row,
                    },
                });
                toggleHighlight(block.temporaryId, false);

                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        };

        // ----------------------------------------
        // RESIZE LOGIC
        // ----------------------------------------

        const isMediaOnlyBlock = block => {
            if (block.type !== ContainerTypes.Multimedia) return false;
            const mp = block.multimediaPart;
            if (!mp) return false;
            const hasImages = Array.isArray(mp.images) && mp.images.length > 0;
            const hasVideo = mp.video && mp.video !== '';
            const hasMedia = hasImages || hasVideo;
            if (!hasMedia) return false;
            const hasText =
                mp.text &&
                mp.text !== '' &&
                mp.text !== null &&
                mp.text !== '<div><br></div>' &&
                mp.text !== '<div></div>';
            const hasButton =
                mp.buttonText && mp.buttonText !== '' && mp.buttonText !== null;
            const hasTitle =
                block.displayTitle && block.title && block.title !== '';
            return !hasText && !hasButton && !hasTitle;
        };

        const isImageOnlyLinkBlock = block => {
            if (block.type !== ContainerTypes.Link) return false;
            const lp = block.linkPart;
            if (!lp) return false;
            if (lp.glyph !== null) return false;
            const hasImage =
                lp.image !== null && lp.image !== undefined && lp.image !== '';
            if (!hasImage) return false;
            const hasText =
                lp.text &&
                lp.text !== '' &&
                lp.text !== null &&
                lp.text !== '<div><br></div>' &&
                lp.text !== '<div></div>';
            const hasButton =
                lp.buttonText && lp.buttonText !== '' && lp.buttonText !== null;
            return !hasText && !hasButton;
        };

        const isSingleImageWithoutBodyContent = block => {
            if (isImageOnlyLinkBlock(block)) {
                return true;
            }

            if (block.type !== ContainerTypes.Multimedia) {
                return false;
            }

            const multimediaPart = block.multimediaPart;
            const hasSingleImage =
                Array.isArray(multimediaPart?.images) &&
                multimediaPart.images.length === 1;
            const hasVideo =
                multimediaPart?.video && multimediaPart.video !== '';
            const hasText =
                multimediaPart?.text &&
                multimediaPart.text !== '' &&
                multimediaPart.text !== '<div><br></div>' &&
                multimediaPart.text !== '<div></div>';
            const hasButton =
                multimediaPart?.buttonText && multimediaPart.buttonText !== '';
            return hasSingleImage && !hasVideo && !hasText && !hasButton;
        };

        const getTitleAdditionalRows = (block, rowHeight) => {
            const hasTitle =
                block.displayTitle && block.title && block.title !== '';
            if (!hasTitle || !rowHeight) {
                return 0;
            }

            const contentElement = contentDivRefs.current[block.temporaryId];
            const titleElement = contentElement?.querySelector(
                '[data-container-title="true"]',
            );
            if (!contentElement || !titleElement) {
                const fallbackTitleChromeHeight = 64;
                return Math.ceil(fallbackTitleChromeHeight / (rowHeight + GAP));
            }

            const contentStyles = window.getComputedStyle(contentElement);
            const titleChromeHeight =
                titleElement.offsetHeight +
                parseFloat(contentStyles.paddingTop || 0) +
                parseFloat(contentStyles.paddingBottom || 0) +
                parseFloat(contentStyles.rowGap || contentStyles.gap || 0);
            return Math.ceil(titleChromeHeight / (rowHeight + GAP));
        };

        const getRatioLockedImageUrl = block => {
            if (!isSingleImageWithoutBodyContent(block)) {
                return null;
            }

            if (block.type === ContainerTypes.Link) {
                return block.linkPart.image;
            }
            return block.multimediaPart.images[0];
        };

        const getMinimumSizeInGrid = item => {
            // Helper to get columns for 873px
            function getColsForWidth(px) {
                const colWidth = selectedResolution / 96;
                return Math.round(px / colWidth);
            }
            switch (item.type) {
                case ContainerTypes.EnrollmentByAge:
                case ContainerTypes.PremiumByPlanType:
                    return { minCols: 24, minRows: 20 };
                case ContainerTypes.Document:
                    return { minCols: 14, minRows: 7 };
                case ContainerTypes.ChildPortal:
                    if (item.childPortalPart?.glyph === null) {
                        // Child with multimedia view
                        return { minCols: 24, minRows: 10 };
                    } else {
                        // Child with icon view
                        return { minCols: 14, minRows: 6 };
                    }
                case ContainerTypes.Link:
                    if (item.acknowledgmentType !== 0) {
                        return { minCols: 24, minRows: 7 };
                    }
                    if (isImageOnlyLinkBlock(item)) {
                        const hasTitle =
                            item.displayTitle &&
                            item.title &&
                            item.title !== '';
                        if (hasTitle) {
                            return { minCols: 24, minRows: 7 };
                        }
                        return { minCols: 7, minRows: 7 };
                    }
                    if (item.linkPart?.glyph === null) {
                        return { minCols: 24, minRows: 10 };
                    }
                    return { minCols: 14, minRows: 7 };
                case ContainerTypes.Table:
                    return { minCols: 24, minRows: 10 };
                case ContainerTypes.Multimedia: {
                    // If the multimediaPart.text contains a table, expand to 873px wide
                    const hasTable =
                        typeof item.multimediaPart?.text === 'string' &&
                        item.multimediaPart.text.includes('<table');
                    if (hasTable) {
                        return { minCols: getColsForWidth(873), minRows: 10 };
                    }
                    if (isMediaOnlyBlock(item)) {
                        const hasMultipleImages =
                            Array.isArray(item.multimediaPart?.images) &&
                            item.multimediaPart.images.length > 1;
                        const hasVideo =
                            item.multimediaPart?.video &&
                            item.multimediaPart.video !== '';
                        const isSingleImage = !hasMultipleImages && !hasVideo;
                        if (isSingleImage) {
                            return { minCols: 7, minRows: 7 };
                        }
                        return hasVideo
                            ? { minCols: 24, minRows: 14 }
                            : { minCols: 24, minRows: 7 };
                    }
                    return { minCols: 24, minRows: 7 };
                }
                default:
                    return { minCols: 24, minRows: 0 }; // Default minimum size for other types
            }
        };

        const handleResizeMouseDown = (e, block) => {
            e.stopPropagation();
            e.preventDefault();
            const initialX = e.clientX;
            const initialY = e.clientY;
            let latestBlock = block;

            const mediaOnly = isMediaOnlyBlock(block);
            const isVideoOnlyMediaOnly =
                mediaOnly &&
                block.multimediaPart?.video &&
                block.multimediaPart.video !== '' &&
                (!Array.isArray(block.multimediaPart?.images) ||
                    block.multimediaPart.images.length === 0);
            const isSingleImageMediaOnly =
                isSingleImageWithoutBodyContent(block) || isVideoOnlyMediaOnly;
            const originalCols = block.end.col - block.start.col + 1;
            const originalRows = block.end.row - block.start.row + 1;
            const imageUrl = getRatioLockedImageUrl(block);
            const imageAspectRatio = imageUrl
                ? mediaAspectRatiosRef.current.get(imageUrl)
                : null;
            if (imageUrl && !imageAspectRatio) {
                getMediaAspectRatio({ images: [imageUrl] });
                return;
            }

            const previousUserSelect = document.body.style.userSelect;
            document.body.style.userSelect = 'none';
            if (activeBlock) {
                setActiveBlock(null);
            }
            const originalBlockSize = { ...block }; // Store the original block size

            const columnWidth = getColumnWidth();
            const rowHeight = columnWidth; // squares

            const aspectRatio = isVideoOnlyMediaOnly
                ? 9 / 16
                : imageAspectRatio || originalRows / originalCols;

            const onMouseMove = moveEvent => {
                const dx = moveEvent.clientX - initialX;
                const dy = moveEvent.clientY - initialY;

                // Calculate new end column and row based on mouse movement
                let newEndCol =
                    block.end.col + Math.floor(dx / (columnWidth + GAP));
                let newEndRow =
                    block.end.row + Math.floor(dy / (rowHeight + GAP));

                // Get the minimum size in columns and rows based on block type
                const { minCols, minRows } = getMinimumSizeInGrid(block);

                // For single-image media-only blocks, lock the aspect ratio
                if (isSingleImageMediaOnly) {
                    const titleAdditionalRows = getTitleAdditionalRows(
                        block,
                        rowHeight,
                    );
                    const minimumImageRows = Math.max(
                        minRows - titleAdditionalRows,
                        1,
                    );
                    const minColsForMinRows = Math.ceil(
                        minimumImageRows / aspectRatio,
                    );
                    const effectiveMinCols = Math.max(
                        minCols,
                        minColsForMinRows,
                    );
                    const newCols = newEndCol - block.start.col + 1;
                    const constrainedCols = Math.max(newCols, effectiveMinCols);
                    newEndCol = block.start.col + constrainedCols - 1;
                    const derivedRows = Math.max(
                        Math.round(constrainedCols * aspectRatio) +
                            titleAdditionalRows,
                        minRows,
                    );
                    newEndRow = block.start.row + derivedRows - 1;
                }

                // Check if the new size is below the minimum size
                const isTooSmall =
                    newEndCol - block.start.col + 1 < minCols ||
                    newEndRow - block.start.row + 1 < minRows;

                // If the new size is too small, adjust the newEndCol and newEndRow accordingly
                if (isTooSmall) {
                    if (newEndCol - block.start.col + 1 < minCols) {
                        newEndCol = block.start.col + minCols - 1;
                    }
                    if (newEndRow - block.start.row + 1 < minRows) {
                        newEndRow = block.start.row + minRows - 1;
                    }
                }

                // Create a new block object with updated end values
                const updatedBlock = {
                    ...block,
                    end: { col: newEndCol, row: newEndRow },
                };

                // For single-image media-only blocks, skip content-height expansion
                if (isSingleImageMediaOnly) {
                    const newHeight =
                        (updatedBlock.end.row - updatedBlock.start.row + 1) *
                            rowHeight +
                        (updatedBlock.end.row - updatedBlock.start.row) * GAP;

                    const finalBlock = { ...updatedBlock };
                    latestBlock = {
                        ...finalBlock,
                        height: newHeight,
                    };

                    if (
                        newEndCol !== block.end.col ||
                        finalBlock.end.row !== block.end.row
                    ) {
                        setRawBlocks(prevBlocks => {
                            const updatedBlocks = prevBlocks.map(b => {
                                if (b.temporaryId === block.temporaryId) {
                                    return {
                                        ...finalBlock,
                                        height: newHeight,
                                    };
                                }
                                return b;
                            });
                            const overlap = isOverlapping(
                                updatedBlock,
                                updatedBlocks,
                            );
                            toggleHighlight(
                                block.temporaryId,
                                overlap,
                                isTooSmall,
                            );
                            return updatedBlocks;
                        });
                    }
                    return;
                }

                // Get the content height after checking
                const contentHeight = checkAndUpdateHeight(block.temporaryId);
                // Calculate the new height based on the updated block
                const newHeight =
                    (updatedBlock.end.row - updatedBlock.start.row + 1) *
                        rowHeight +
                    (updatedBlock.end.row - updatedBlock.start.row) * GAP;

                // Use contentHeight if it's greater than the calculated newHeight
                const finalHeight = Math.max(newHeight, contentHeight);

                // Calculate the number of rows that fit into the finalHeight
                const rowsFittingInFinalHeight = Math.floor(
                    finalHeight / (rowHeight + GAP),
                );

                // Update the finalBlock's end row based on the calculated number of rows
                const finalBlock = {
                    ...block,
                    end: {
                        col: newEndCol,
                        row: block.start.row + rowsFittingInFinalHeight,
                    },
                };
                latestBlock = { ...finalBlock, height: finalHeight };

                // Only update the state if the new block is different
                if (
                    newEndCol !== block.end.col ||
                    finalBlock.end.row !== block.end.row
                ) {
                    setRawBlocks(prevBlocks => {
                        const updatedBlocks = prevBlocks.map(b => {
                            if (b.temporaryId === block.temporaryId) {
                                return { ...finalBlock, height: finalHeight };
                            } else {
                                return b;
                            }
                        });
                        const overlap = isOverlapping(
                            updatedBlock,
                            updatedBlocks,
                        );
                        toggleHighlight(block.temporaryId, overlap, isTooSmall);
                        return updatedBlocks;
                    });
                }
            };

            const onMouseUp = () => {
                let committedBlock = latestBlock;
                if (isSingleImageMediaOnly) {
                    const { minRows } = getMinimumSizeInGrid(latestBlock);
                    const finalCols =
                        latestBlock.end.col - latestBlock.start.col + 1;
                    const titleAdditionalRows = getTitleAdditionalRows(
                        latestBlock,
                        rowHeight,
                    );
                    const finalRows = Math.max(
                        Math.round(finalCols * aspectRatio) +
                            titleAdditionalRows,
                        minRows,
                    );
                    committedBlock = {
                        ...latestBlock,
                        end: {
                            ...latestBlock.end,
                            row: latestBlock.start.row + finalRows - 1,
                        },
                    };
                }

                setRawBlocks(prevBlocks =>
                    prevBlocks.map(currentBlock =>
                        currentBlock.temporaryId === block.temporaryId
                            ? committedBlock
                            : currentBlock,
                    ),
                );
                handleUpdateContainer(committedBlock.temporaryId, {
                    start: {
                        col: committedBlock.start.col,
                        row: committedBlock.start.row,
                    },
                    end: {
                        col: committedBlock.end.col,
                        row: committedBlock.end.row,
                    },
                });
                toggleHighlight(block.temporaryId, false);

                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                document.body.style.userSelect = previousUserSelect;
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        };

        // Function to trigger resizing from the Component
        const triggerResize = block => {
            const blockElement = document.getElementById(
                `block-${block.temporaryId}`,
            );
            if (blockElement) {
                // Simulate mouse down on the resizer
                const mouseDownEvent = new MouseEvent('mousedown', {
                    clientX: blockElement.getBoundingClientRect().right,
                    clientY: blockElement.getBoundingClientRect().bottom,
                    bubbles: true,
                    cancelable: true,
                });
                blockElement.dispatchEvent(mouseDownEvent);
            }
        };

        // ----------------------------------------
        // HELPERS
        // ----------------------------------------
        const getColumnWidth = () => {
            const totalGapWidth = GAP * (COLUMN_COUNT - 1);
            return (GRID_WIDTH - totalGapWidth) / COLUMN_COUNT;
        };

        const isOverlapping = (candidateBlock, blocks) => {
            return blocks.some(b => {
                if (b.temporaryId === candidateBlock.temporaryId) return false;
                const noOverlap =
                    candidateBlock.end.col < b.start.col ||
                    candidateBlock.start.col > b.end.col ||
                    candidateBlock.end.row < b.start.row ||
                    candidateBlock.start.row > b.end.row;
                return !noOverlap;
            });
        };

        const toggleHighlight = (
            blockId,
            isOverlap = false,
            isTooSmall = false,
        ) => {
            const blockElement = document.getElementById(`overlay-${blockId}`);
            if (!blockElement) return;

            if (isTooSmall || isOverlap) {
                blockElement.style.visibility = 'visible'; // Red for too small or overlap
            } else {
                blockElement.style.visibility = 'hidden'; // Default color
            }
        };

        const [activeBlock, setActiveBlock] = useState(null);

        const contextMenuRef = useRef(null);

        const toggleContextMenu = block => {
            if (activeBlock && activeBlock.temporaryId === block.temporaryId) {
                setActiveBlock(null); // Close the context menu if it's already open for this block
            } else {
                setActiveBlock(block); // Open the context menu for the clicked block
            }
        };

        useEffect(() => {
            const handleClickOutside = event => {
                setActiveBlock(null);
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        // Handlers for context menu actions
        const handlePropertiesClick = block => {
            setActiveBlock(null); // Close the context menu if it's already open for this block
            handleLaunchModal(block);
        };

        const handleDeleteClick = deleteContainer => {
            setActiveBlock(null); // Close the context menu if it's already open for this block

            // If id is not null, means this container already exists in the database and needs to be deleted
            if (deleteContainer.id !== null) {
                // Add the container id to the deletedContainers array
                deleteContainers(prevDeleteContainers => [
                    ...prevDeleteContainers,
                    deleteContainer.id,
                ]);
            }
            // Remove the container from newContainers
            setNewContainers(prevNewContainers =>
                prevNewContainers.filter(
                    container =>
                        container.temporaryId !== deleteContainer.temporaryId,
                ),
            );
            // Remove the container from mobile layout
            setMobileRenderArray(prevIds =>
                prevIds.filter(id => id !== deleteContainer.temporaryId),
            );
        };

        const getPaddingContainer = (isChildPortalWithIcon, block) => {
            switch (true) {
                case block.type === ContainerTypes.Document:
                    return '20px 20px';
                case block.type === ContainerTypes.Multimedia &&
                    block.title &&
                    (!block?.multimediaPart?.text === '' ||
                        !block?.multimediaPart?.text === null):
                    return '20px';
                case block.type === ContainerTypes.Multimedia &&
                    block.title &&
                    (block?.multimediaPart?.text === '' ||
                        block?.multimediaPart?.text === null):
                    return '20px 20px 0';
                case block.type === ContainerTypes.Multimedia &&
                    (!block.title || block.title === ''):
                    return '0 20px 20px';
                case block.type === ContainerTypes.Multimedia &&
                    (!block.title || block.title === '') &&
                    (block?.multimediaPart?.text === '' ||
                        block?.multimediaPart?.text === null):
                    return '0 20px 0';
                case isChildPortalWithIcon:
                    return '20px';
                case block.acknowledgmentType !== 0:
                    return '46px 1.5rem 1.5rem 1.5rem';
                case block.type === ContainerTypes.ChildPortal &&
                    block.title &&
                    (!block?.childPortalPart?.text === '' ||
                        !block?.childPortalPart?.text === null):
                    return '20px';
                case block.type === ContainerTypes.ChildPortal &&
                    block.title &&
                    (block?.childPortalPart?.text === '' ||
                        block?.childPortalPart?.text === null):
                    return '20px 20px 0';
                case block.type === ContainerTypes.ChildPortal &&
                    (!block.title || block.title === ''):
                    return '0 20px 20px';
                case block.type === ContainerTypes.ChildPortal &&
                    (!block.title || block.title === '') &&
                    (block?.childPortalPart?.text === '' ||
                        block?.childPortalPart?.text === null):
                    return '0 20px 0';
                case block.type === ContainerTypes.Link &&
                    block.title &&
                    (!block?.linkPart?.text === '' ||
                        !block?.linkPart?.text === null):
                    return '20px';
                case block.type === ContainerTypes.Link &&
                    block.title &&
                    (block?.linkPart?.text === '' ||
                        block?.linkPart?.text === null):
                    return '20px 20px 0';
                case block.type === ContainerTypes.Link &&
                    (!block.title || block.title === ''):
                    return '0 20px 20px';
                case block.type === ContainerTypes.Link &&
                    (!block.title || block.title === '') &&
                    (block?.linkPart?.text === '' ||
                        block?.linkPart?.text === null):
                    return '0 20px 0';
                // asset upload
                case block.type === ContainerTypes.AssetUpload:
                    return '56px 20px 20px';
                default:
                    return '20px';
            }
        };

        // ----------------------------------------
        // MOBILE
        // ----------------------------------------

        const [mobileItems, setMobileItems] = useState([]);

        // Function to check if two arrays are different in terms of content and order
        const arraysAreDifferent = (arr1, arr2) => {
            if (arr1.length !== arr2.length) return true;

            for (let i = 0; i < arr1.length; i++) {
                if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
                    return true;
                }
            }
            return false;
        };

        useEffect(() => {
            // Function to check if an object is empty
            const isEmptyObject = obj => {
                return (
                    obj &&
                    typeof obj === 'object' &&
                    Object.keys(obj).length === 0
                );
            };

            // Check if newContainers is a valid array and contains at least one non-empty object
            const hasValidContainers =
                Array.isArray(newContainers) &&
                newContainers.length > 0 &&
                newContainers.some(
                    container => container?.start?.row !== undefined,
                ) &&
                newContainers.some(container => !isEmptyObject(container));

            // Check if mobileRenderArray and fetchedMobileLayout are different
            const layoutsAreDifferent = arraysAreDifferent(
                mobileRenderArray,
                mobileConfig,
            );

            // If there are valid new containers, auto layout is enabled, and layouts are different, reset the mobile layout
            if (hasValidContainers && autoMobileLayout && layoutsAreDifferent) {
                handleResetMobileLayout();
            }

            // If there are no valid new containers, clear the mobile items
            if (!hasValidContainers) {
                setMobileItems([]);
            }
        }, [newContainers]);

        /*useEffect(() => {
        if (mobileRenderArray.length > 0 && newContainers.length > 0) {
            const items = mobileRenderArray.map(id =>
                newContainers.find(container => container.id === id)
            ).filter(item => item !== undefined); // Filter out any undefined values just in case
            setMobileItems(items);
        }
        //saveMobileLayout(mobileRenderArray);
    }, [mobileRenderArray, newContainers]);*/

        useEffect(() => {
            if (mobileRenderArray.length > 0 && newContainers.length > 0) {
                const items = mobileRenderArray
                    .map(id =>
                        newContainers.find(container =>
                            container.id !== null
                                ? container.id === id
                                : container.temporaryId === id,
                        ),
                    )
                    .filter(item => item !== undefined);
                setMobileItems(items);
            } else {
                setMobileItems([]);
            }
        }, [mobileRenderArray, newContainers]);

        const [hoveredIndex, setHoveredIndex] = useState(null);
        const [draggedMobileIndex, setDraggedMobileIndex] = useState(null);

        const handleDragStart = (e, index) => {
            setAutoMobileLayout(false);
            setDraggedMobileIndex(index);
            e.dataTransfer.setData('text/plain', index);
            e.dataTransfer.effectAllowed = 'move';
        };

        const handleDrop = (e, index) => {
            const draggedIndex = e.dataTransfer.getData('text/plain');
            const updatedItems = [...mobileItems];
            const [movedItem] = updatedItems.splice(draggedIndex, 1);

            let finalIndex = index;
            if (draggedIndex < index) {
                finalIndex = index - 1;
            }
            // Insert the moved item at the new index
            updatedItems.splice(finalIndex, 0, movedItem);
            // Set mobileRenderArray with the IDs of the updatedItems
            const updatedIds = updatedItems.map(item => item.temporaryId);
            setMobileRenderArray(updatedIds);
            setHoveredIndex(null);
            setDraggedMobileIndex(null);
        };

        const handleDragOver = (e, position) => {
            e.preventDefault(); // Prevent default to allow drop
            setHoveredIndex(position);
        };

        // *******************************************************
        // *******************************************************

        /* AUTO-SAVE INTERVAL SETUP */
        const containersRef = useRef(containers);
        const newContainersRef = useRef(newContainers);
        const mobileConfigRef = useRef(mobileConfig);
        const mobileRenderArrayRef = useRef(mobileRenderArray);
        const isSavingRef = useRef(isSaving);

        useEffect(() => {
            containersRef.current = containers;
            setIsSaving(false); // exiting save state after containers have been updated for refresh (last step of save grid)
        }, [containers]);

        useEffect(() => {
            newContainersRef.current = newContainers;
        }, [newContainers]);

        useEffect(() => {
            mobileConfigRef.current = mobileConfig;
        }, [mobileConfig]);

        useEffect(() => {
            mobileRenderArrayRef.current = mobileRenderArray;
        }, [mobileRenderArray]);

        useEffect(() => {
            isSavingRef.current = isSaving;
        }, [isSaving]);

        useEffect(() => {
            isActivePageRef.current = isActivePage;
        }, [isActivePage]);

        useEffect(() => {
            hasDraftRef.current = hasDraft;
        }, [hasDraft]);

        const handleSaveLayoutGridRef = useRef(handleSaveLayoutGrid);
        useEffect(() => {
            handleSaveLayoutGridRef.current = handleSaveLayoutGrid;
        });

        useEffect(() => {
            const intervalId = setInterval(
                async () => {
                    if (!isSavingRef.current) {
                        const hasChanges = await checkForChanges();
                        if (hasChanges) {
                            handleSaveLayoutGridRef.current(true);
                        } else {
                            Toast.info(
                                getText('msg_fa7e09229620'),
                            );
                        }
                    }
                },
                5 * 60 * 1000,
            ); // 5 minutes

            return () => clearInterval(intervalId);
        }, []); // Runs once on mount

        return (
            <div
                style={{
                    display: 'flex',
                    padding: '0px',
                    height: '100%',
                    overflow: 'auto',
                }}
            >
                {propertiesModal}
                {isAddContainerOpen && (
                    <div
                        style={{
                            position: 'absolute',
                            left: '0',
                            right: '0',
                            top: '0',
                            bottom: '0',
                            margin: 'auto',
                            zIndex: '99',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <div
                            className="card shadow-sm border-light"
                            style={{
                                width: '557px',
                                height: 'fit-content',
                                display: 'flex',
                            }}
                        >
                            <div
                                id="header"
                                style={{
                                    height: 'fit-content',
                                    width: '100%',
                                    padding: '28px 28px 0 28px',
                                    marginBottom: '20px',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '20px',
                                        fontWeight: '600',
                                        marginBottom: '10px',
                                    }}
                                >
                                    {getText('msg_95fe71eab500')}
                                </p>
                                <p
                                    style={{
                                        color: 'var(--iNGAGED-Sub-Text, rgba(47, 43, 67, 0.60))',
                                        fontWeight: '500',
                                    }}
                                >
                                    {getText('msg_88f0ce4089a8')}
                                </p>

                                <div
                                    onClick={() => setIsAddContainerOpen(false)}
                                    className="d-flex align-items-center justify-content-center"
                                    style={{
                                        position: 'absolute',
                                        right: '0',
                                        top: '14px',
                                        width: '100px',
                                        height: '20px',
                                    }}
                                >
                                    <span
                                        type="button"
                                        className="ing-text-buttons-save absolute-position pointer-cursor"
                                        style={{
                                            color: 'rgba(102, 112, 133, 1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                        }}
                                    >
                                        <i
                                            className="bi bi-x"
                                            style={{ fontSize: '1.2rem' }}
                                        />
                                        {getText('msg_bbfa773e5a63')}
                                    </span>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: 'fit-content',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    margin: '0 auto',
                                }}
                            >
                                <p
                                    onClick={() => setTemplatesType('Custom')}
                                    style={{
                                        width: '186px',
                                        textAlign: 'center',
                                        borderBottom:
                                            templatesType === 'Custom'
                                                ? '2px solid var(--iNGAGED-Primary-Color)'
                                                : 'none',
                                        padding: '18px',
                                        color:
                                            templatesType === 'Custom'
                                                ? 'var(--iNGAGED-Primary-Color)'
                                                : 'inherit',
                                        fontWeight:
                                            templatesType === 'Custom'
                                                ? '600'
                                                : '500',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {getText('msg_081ae3fdc403')}
                                </p>
                                <p
                                    onClick={() =>
                                        setTemplatesType('Templates')
                                    }
                                    style={{
                                        width: '186px',
                                        textAlign: 'center',
                                        borderBottom:
                                            templatesType === 'Templates'
                                                ? '2px solid var(--iNGAGED-Primary-Color)'
                                                : 'none',
                                        padding: '18px',
                                        color:
                                            templatesType === 'Templates'
                                                ? 'var(--iNGAGED-Primary-Color)'
                                                : 'inherit',
                                        fontWeight:
                                            templatesType === 'Templates'
                                                ? '600'
                                                : '500',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {getText('msg_f25b700ed9f0')}
                                </p>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    height: '100%',
                                    justifyContent: 'space-between',
                                    margin: '45px 0',
                                    padding: '0px 70px',
                                    gap: '20px',
                                }}
                            >
                                {templates
                                    .filter(
                                        template =>
                                            (templatesType === 'Custom' &&
                                                template.templateScope === 0) ||
                                            (templatesType === 'Templates' &&
                                                template.templateScope !== 0),
                                    )
                                    .map(template => (
                                        <div
                                            onClick={() => {
                                                launchContainerCreation(
                                                    template,
                                                );
                                            }}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '180px',
                                                height: '30px',
                                            }}
                                            key={template.id}
                                        >
                                            <span
                                                style={{
                                                    width: '30px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    marginRight: '10px',
                                                }}
                                            >
                                                {getTemplateIcon(template.type)}
                                            </span>
                                            <p className="templateLabel">
                                                {template.name}
                                            </p>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                )}
                <div className="main-area">
                    {isLoading && (
                        <div className="loadingOverlay">
                            <Spinner width={50} className="loadingSpinner" />
                        </div>
                    )}

                    {selectedResolution > 786 &&
                        (newContainers.length <= 0 ? (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: ' 100vh',
                                }}
                            >
                                <div className="start-building-container">
                                    <div className="icon-container">
                                        <i
                                            className="bi bi-columns-gap"
                                            style={{ color: '#1366FF' }}
                                        ></i>
                                    </div>
                                    <span
                                        style={{
                                            color: '#333',
                                            textAlign: 'center',
                                            fontSize: '20px',
                                            fontWeight: '700',
                                        }}
                                    >
                                        {getText('msg_f34da2c6abe8')}
                                    </span>
                                    <p style={{ color: '#667085' }}>
                                        {getText('msg_790bb0c3d43b')}
                                    </p>
                                    <span
                                        onClick={() => {
                                            setIsAddContainerOpen(true);
                                        }}
                                        type="button"
                                        className="ing-popup-blue-positive"
                                    >
                                        <i className="bi bi-plus-lg" />
                                        {getText('msg_61cc55aa0453')}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div
                                className="freeform-grid"
                                style={{
                                    height: `${gridHeight}px`,
                                    width: `${GRID_WIDTH}px`,
                                }}
                            >
                                {items.map(item => (
                                    <div key={item} className="grid-item"></div>
                                ))}

                                {contentBlocks.map(block => {
                                    const startCol = block.start.col + 1;
                                    const endCol = block.end.col + 2; // +2 because grid "end" is exclusive
                                    const startRow = block.start.row + 1;
                                    const endRow = block.end.row + 2;

                                    // Get the minimum size in columns and rows based on block type
                                    const { minCols, minRows } =
                                        getMinimumSizeInGrid(block);

                                    // Calculate current size in columns and rows
                                    const currentCols =
                                        block.end.col - block.start.col + 1;
                                    const currentRows =
                                        block.end.row - block.start.row + 1;

                                    // Determine the background color based on the current size
                                    const isTooSmall =
                                        currentCols < minCols ||
                                        currentRows < minRows;

                                    // Local variables for background color and icon state
                                    let backgroundColor =
                                        block.childPortalPart
                                            ?.backgroundColor || 'white'; // Set a default color if needed
                                    let isChildPortalWithIcon = false;

                                    if (
                                        block.type ===
                                            ContainerTypes.ChildPortal &&
                                        block.childPortalPart !== null
                                    ) {
                                        if (
                                            block.childPortalPart.glyph !== null
                                        ) {
                                            isChildPortalWithIcon = true;
                                        }
                                    } else if (
                                        block.type === ContainerTypes.Link &&
                                        block.linkPart !== null
                                    ) {
                                        if (block.linkPart.glyph !== null) {
                                            backgroundColor =
                                                block.linkPart.backgroundColor;
                                            isChildPortalWithIcon = true;
                                        }
                                    }

                                    const isOverlapping = overlappingIds.has(
                                        block.temporaryId,
                                    );

                                    return (
                                        <div
                                            key={block.temporaryId}
                                            id={`block-${block.temporaryId}`}
                                            className="content-block"
                                            onMouseDown={e =>
                                                handleMouseDown(e, block)
                                            }
                                            style={{
                                                gridColumn: `${startCol} / ${endCol}`,
                                                gridRow: `${startRow} / ${endRow}`,
                                                width: `${block.width}px`,
                                                height: `${block.height}px`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                position: 'absolute',
                                                zIndex: isOverlapping
                                                    ? 2
                                                    : 'auto',
                                            }}
                                        >
                                            {isOverlapping && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        zIndex: 500,
                                                        pointerEvents: 'none',
                                                        borderRadius: '10px',
                                                    }}
                                                />
                                            )}
                                            {block.isNew && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        backgroundColor:
                                                            'rgba(255, 255, 255, 0.6)',
                                                        zIndex: 3,
                                                    }}
                                                >
                                                    <Spinner width={32} />
                                                </div>
                                            )}
                                            <Component
                                                ID={block.id}
                                                Name={block.name}
                                                Subtitle={block.title}
                                                ContentTypeID={block.type}
                                                HelpBoxText={block.helpBoxText}
                                                AcknowledgmentReq={
                                                    block.acknowledgmentType
                                                }
                                                AcknowledgmentText={
                                                    block.acknowledgmentText
                                                }
                                                editMode={true}
                                                width={block.width}
                                                height={block.height}
                                                dueDate={block.dueDate}
                                                data={block}
                                                navigationStack={[]}
                                                displayTitle={
                                                    block.displayTitle
                                                }
                                                isMobile={false}
                                                contextMenuRef={contextMenuRef}
                                                activeBlock={activeBlock}
                                                block={block}
                                                contentDivRefs={contentDivRefs}
                                                handleResizeMouseDown={
                                                    handleResizeMouseDown
                                                }
                                                handlePropertiesClick={
                                                    handlePropertiesClick
                                                }
                                                handleDeleteClick={
                                                    handleDeleteClick
                                                }
                                                toggleContextMenu={
                                                    toggleContextMenu
                                                }
                                            ></Component>
                                        </div>
                                    );
                                })}

                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: `${GRID_WIDTH}px`,
                                        height: `${gridHeight}px`,
                                        opacity: 0.3,
                                        pointerEvents: 'none',
                                        zIndex: 10,
                                    }}
                                >
                                    {contentBlocks.map(block => (
                                        <div
                                            key={`overlay-${block.temporaryId}`}
                                            id={`overlay-${block.temporaryId}`}
                                            className="overlay"
                                            style={{
                                                left: `${block.start.col * (cellSize + GAP)}px`,
                                                top: `${block.start.row * (cellSize + GAP)}px`,
                                                width: `${block.width}px`,
                                                height: `${block.height}px`,
                                            }}
                                        />
                                    ))}
                                </div>
                                {/* Red border overlays for overlapping containers */}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: `${GRID_WIDTH}px`,
                                        height: `${gridHeight}px`,
                                        pointerEvents: 'none',
                                        zIndex: 500,
                                    }}
                                >
                                    {contentBlocks
                                        .filter(block =>
                                            overlappingIds.has(
                                                block.temporaryId,
                                            ),
                                        )
                                        .map(block => (
                                            <div
                                                key={`overlap-border-${block.temporaryId}`}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${block.start.col * (cellSize + GAP)}px`,
                                                    top: `${block.start.row * (cellSize + GAP)}px`,
                                                    width: `${block.width}px`,
                                                    height: `${block.height}px`,
                                                    border: '1px solid red',
                                                    backgroundColor:
                                                        'rgba(255, 0, 0, 0.2)',
                                                    borderRadius: '10px',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                        ))}
                                </div>
                            </div>
                        ))}

                    {selectedResolution <= 786 && (
                        <div
                            style={{
                                width: 'fit-content',
                                height: '100%',
                                margin: '0 auto',
                                padding: '10px 0',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <p
                                style={{
                                    whiteSpace: 'nowrap',
                                    color: '#667085',
                                    fontFamily: 'Inter',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.32px',
                                    textAlign: 'left',
                                    paddingBottom: '20px',
                                }}
                            >
                                Drag and drop containers to arrange for Mobile
                                view.
                                <br />
                                Adding or editing content must be done in
                                Desktop Mode.
                            </p>
                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'row',
                                }}
                            >
                                <div
                                    style={{
                                        padding: '0 10px',
                                        border: '1px solid #979797',
                                        height: '100%',
                                        overflowY: 'auto',
                                        width: '375px',
                                    }}
                                >
                                    <div
                                        style={{
                                            height:
                                                hoveredIndex === -1
                                                    ? '60px'
                                                    : '20px',
                                            backgroundColor:
                                                hoveredIndex === -1
                                                    ? '#1366FF30'
                                                    : 'transparent',
                                            borderRadius: '8px',
                                            margin:
                                                hoveredIndex === -1
                                                    ? '10px 0'
                                                    : '0',
                                        }}
                                        onDragOver={e => handleDragOver(e, -1)}
                                        onDrop={e => handleDrop(e, 0)} // Drop as the first item
                                    />
                                    {mobileItems.map((block, index) => (
                                        <div key={block.temporaryId}>
                                            <div
                                                draggable
                                                onDragStart={e =>
                                                    handleDragStart(e, index)
                                                }
                                                onDragEnd={() => {
                                                    setDraggedMobileIndex(null);
                                                    setHoveredIndex(null);
                                                }}
                                                onDragOver={handleDragOver}
                                                onDrop={e =>
                                                    handleDrop(e, index + 1)
                                                }
                                                style={{
                                                    width: '100%',
                                                    height: 'fit-content',
                                                    opacity:
                                                    draggedMobileIndex === index
                                                            ? 0.4
                                                            : 1,
                                                    transition: 'opacity 0.2s',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        pointerEvents: 'none',
                                                    }}
                                                >
                                                    <Component
                                                        ID={block.id}
                                                        Name={block.name}
                                                        Subtitle={block.title}
                                                    ContentTypeID={block.type}
                                                        HelpBoxText={
                                                            block.helpBoxText
                                                        }
                                                        AcknowledgmentReq={
                                                            block.acknowledgmentType
                                                        }
                                                        AcknowledgmentText={
                                                            block.acknowledgmentText
                                                        }
                                                        editMode={true}
                                                        width={block.width}
                                                        height={block.height}
                                                        dueDate={block.dueDate}
                                                        data={block}
                                                        block={block}
                                                        contentDivRefs={
                                                            contentDivRefs
                                                        }
                                                        navigationStack={[]}
                                                        displayTitle={
                                                            block.displayTitle
                                                        }
                                                        isMobile={true}
                                                    />
                                                </div>
                                            </div>

                                            {/* Clear div for dropping between items */}
                                        {index + 1 !== mobileItems.length && (
                                                <div
                                                    style={{
                                                        height:
                                                            hoveredIndex ===
                                                            index + 1
                                                                ? '60px'
                                                                : '20px',
                                                        backgroundColor:
                                                            hoveredIndex ===
                                                            index + 1
                                                                ? '#1366FF30'
                                                                : 'transparent',
                                                        borderRadius: '8px',
                                                        margin:
                                                            hoveredIndex ===
                                                            index + 1
                                                                ? '10px 0'
                                                                : '0',
                                                    }}
                                                    onDragOver={e => {
                                                        handleDragOver(
                                                            e,
                                                            index + 1,
                                                        );
                                                    }}
                                                    onDrop={e =>
                                                        handleDrop(e, index + 1)
                                                    } // Drop after this item
                                                />
                                            )}
                                        </div>
                                    ))}
                                    {/* Clear div for dropping below the last item */}
                                    <div
                                        style={{
                                            height:
                                            hoveredIndex === mobileItems.length
                                                    ? '60px'
                                                    : '20px',
                                            backgroundColor:
                                            hoveredIndex === mobileItems.length
                                                    ? '#1366FF30'
                                                    : 'transparent',
                                            borderRadius: '8px',
                                            margin:
                                            hoveredIndex === mobileItems.length
                                                    ? '10px 0'
                                                    : '0',
                                        }}
                                        onDragOver={e => {
                                        handleDragOver(e, mobileItems.length);
                                        }}
                                        onDrop={e =>
                                            handleDrop(e, mobileItems.length)
                                        } // Drop as the last item
                                    />
                                </div>
                                <div
                                    style={{
                                        color: '#2F2B4399',
                                        textAlign: 'center',
                                        height: 'fit-content',
                                    }}
                                >
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

export default NewPortalLayout;
